import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  isDevMode,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { skip } from 'rxjs/operators';
import { TourFeature } from '../../config/tour-feature.enum';
import { TourStateStatus } from '../../config/tour-state.enum';
import { TOUR_PREREQUISITES } from '../../config/tour-prerequisites';
import { HelpVideo, helpVideoFor } from '../../config/help-video.config';
import { HelpVideoDialogComponent } from '../../components/help-video-dialog/help-video-dialog.component';
import { TourStateSnapshot } from '../../models/tour-state-snapshot.model';
import { TourRegistry } from '../../services/tour-registry.service';
import { TourStateStore } from '../../services/tour-state-store.service';
import { TourCoordinator } from '../../services/tour-coordinator.service';
import { AuthService } from 'src/app/modules/auth/services/auth/auth.service';
import { FeatureFlagsService } from '../../services/feature-flags.service';
import {
  OnboardingProgress,
  OnboardingProgressService,
} from '../../services/onboarding-progress.service';
import { UserPermissionsService } from 'src/app/modules/employee/services/user-permissions.service';
import { UserPermissionConfig } from 'src/app/modules/employee/model/user-permissions.model';
import { hasAllPermissions } from '../../services/permission-gate';
import { UserSettingsService } from 'src/app/modules/settings/user-settings/services/user-settings.service';
import { UserConfig } from 'src/app/modules/settings/user-settings/model/user-settings.model';
import { ConfirmationDialogComponent } from 'src/app/core/component/confirmation-dialog/confirmation-dialog.component';
import { AdminUserSettingsDialogComponent } from 'src/app/modules/admin/component/admin-user-settings-dialog/admin-user-settings-dialog.component';
import { HelpHubStateService } from 'src/app/help/help-hub/help-hub-state.service';
import { XdsIconButtonComponent } from 'src/app/shared/ui/icon-button/icon-button.component';
import { XdsButtonComponent } from 'src/app/shared/ui/button/button.component';
import { XdsProgressComponent } from 'src/app/shared/ui/progress/progress.component';
import { XdsTooltipDirective } from 'src/app/shared/ui/tooltip/tooltip.directive';
import { xdsDialogConfig } from 'src/app/shared/ui/dialog-shell/dialog-config';

/**
 * Modernisierter Onboarding-Checklist-FAB: dauerhaft erreichbarer
 * Fortschrittsanzeiger fuer Admins/TeamLeader.
 *
 * UX-Prinzipien:
 * - **Ruhig**: dezenter FAB links unten (kollidiert nicht mit dem Sim-FAB
 *   rechts), gedeckte Farben, keine Pulse-Animation
 * - **Persistent**: bleibt immer erreichbar, auch nach Abschluss aller
 *   Touren (dann als Replay-Quelle)
 * - **Resume-fokussiert**: fuer jede Tour zeigt der CTA das richtige
 *   Verb — "Starten", "Fortsetzen", "Nochmal machen"
 * - **Progressive Disclosure**: FAB → Panel; keine Drill-Down-Ebenen
 *
 * Sichtbarkeit wird pro Nutzer ueber `UserConfig.Dashboard.ShowOnboardingAssistant`
 * gesteuert (Fallback: Company-Default `UISettings.ShowOnboardingAssistant`, via
 * FeatureFlagsService) sowie die Audience.
 */
@Component({
  selector: 'app-onboarding-checklist-fab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    XdsIconButtonComponent,
    XdsButtonComponent,
    XdsProgressComponent,
    XdsTooltipDirective,
  ],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *ngIf="visible">
      <!-- FAB-Variante: progress-Pill solange noch nicht alle Touren done.
           Bestehende UX, kein Verhaltenswechsel. -->
      <button
        *ngIf="!embedded && !open && !allDone"
        type="button"
        class="checklist-fab"
        [class.is-pulsing]="pulseActive"
        (click)="toggle()"
        [attr.aria-label]="'ONBOARDING_CHECKLIST.OPEN' | translate">
        <i class="fa-light fa-list-check"></i>
        <span class="checklist-fab-label">
          {{ 'ONBOARDING_CHECKLIST.FAB_LABEL' | translate }}
        </span>
        <span
          *ngIf="openCount > 0"
          class="checklist-fab-badge"
          [attr.aria-label]="openCount + ' offen'">
          {{ openCount }}
        </span>
      </button>
      <!-- FAB-Variante: kompakter Done-Pill, sobald alle Touren mindestens
           einmal echt durchlaufen wurden. Gleiche Position (Muskelgedaecht-
           nis), aber kein Counter, kein Label — nur ein Erfolgs-Haekchen.
           Klick oeffnet das Done-Panel zum Replay einzelner Touren. -->
      <button
        *ngIf="!embedded && !open && allDone"
        type="button"
        class="checklist-fab is-done-pill"
        (click)="toggle()"
        [attr.aria-label]="'ONBOARDING_CHECKLIST.DONE_FAB_LABEL' | translate"
        [xdsTooltip]="'ONBOARDING_CHECKLIST.DONE_FAB_LABEL' | translate"
        xdsTooltipPosition="right">
        <i class="fa-solid fa-circle-check" aria-hidden="true"></i>
      </button>

      <!--
        Resume-Hinweis: erscheint kurz nach einem X-Klick auf den Tour-
        Popover. Sitzt direkt ueber dem FAB und verbindet visuell die
        Pause-Geste mit dem Resume-Punkt. Auto-Dismiss nach 3.6 s, oder
        sofort beim FAB-Klick. Wird nicht gezeigt, wenn das Panel schon
        offen ist — der User sieht den Resume-CTA dann ohnehin direkt.
      -->
      <div
        *ngIf="!embedded && toastVisible && !open"
        class="checklist-pause-toast"
        role="status"
        aria-live="polite">
        <i class="fa-solid fa-pause-circle" aria-hidden="true"></i>
        <span class="checklist-pause-toast-text">
          {{ 'ONBOARDING_CHECKLIST.PAUSE_TOAST' | translate }}
        </span>
        <i
          class="fa-solid fa-arrow-down checklist-pause-toast-arrow"
          aria-hidden="true"></i>
      </div>

      <aside
        *ngIf="open || embedded"
        class="checklist-panel"
        data-testid="onboarding-checklist-panel"
        [class.is-all-done]="allDone"
        [class.embedded]="embedded"
        role="complementary"
        [attr.aria-label]="'ONBOARDING_CHECKLIST.TITLE' | translate">
        <!-- Header bewusst minimal: nur Titel + Close. Keine Eyebrow,
             keine Restzeit-Anzeige — die ETA sitzt direkt am "Als Naechstes"-
             Item, dort wo sie konkret zaehlt. Doppelangabe ist Laerm.
             Bei 6/6: Crossfade-Twin tauscht "Setup-Checkliste" gegen
             "Einrichtung abgeschlossen" + Datumsstempel. Crossfade ueber
             absolut gestapelte Spans (siehe CSS), damit Layout nicht
             springt waehrend der 400ms-Transition. -->
        <header class="checklist-panel-header">
          <h3
            class="checklist-panel-title"
            [class.is-crossfade]="allDone">
            <span class="title-default">
              {{ 'ONBOARDING_CHECKLIST.TITLE' | translate }}
            </span>
            <span class="title-done">
              <span class="title-done-main">
                <i
                  class="fa-solid fa-circle-check title-done-icon"
                  aria-hidden="true"></i>
                {{ 'ONBOARDING_CHECKLIST.TITLE_DONE' | translate }}
              </span>
              <span
                *ngIf="setupCompletedAtRelative"
                class="title-done-date"
                [xdsTooltip]="setupCompletedAtAbsolute || ''"
                xdsTooltipPosition="bottom"
                [xdsTooltipShowDelay]="600">
                {{ setupCompletedAtRelative }}
              </span>
            </span>
          </h3>
          <!-- Header-Aktionen: ⋮-Overflow (Assistent verwalten/ausblenden)
               + Close. Das Ausblenden sitzt bewusst hier oben im Header der
               "Setup-Checkliste" — sofort sichtbar (kein Scrollen) und
               eindeutig auf die Einrichtung bezogen, nie auf den Fragen-Tab.
               Ersetzt den frueheren, leicht zu uebersehenden Footer-Button. -->
          <div class="checklist-panel-header-actions">
            <div class="checklist-menu-wrap">
              <button
                type="button"
                class="checklist-panel-kebab"
                [class.is-open]="manageMenuOpen"
                (click)="toggleManageMenu($event)"
                aria-haspopup="menu"
                [attr.aria-expanded]="manageMenuOpen"
                [attr.aria-label]="'ONBOARDING_CHECKLIST.MANAGE_MENU' | translate">
                <i class="fa-solid fa-ellipsis-vertical" aria-hidden="true"></i>
              </button>
              <div
                *ngIf="manageMenuOpen"
                class="checklist-menu"
                role="menu"
                [attr.aria-label]="'ONBOARDING_CHECKLIST.MANAGE_MENU' | translate">
                <button
                  type="button"
                  class="checklist-menu-item"
                  role="menuitem"
                  (click)="requestHideAssistant()">
                  <i class="fa-light fa-eye-slash" aria-hidden="true"></i>
                  <span>{{ 'ONBOARDING_CHECKLIST.HIDE_SETUP' | translate }}</span>
                </button>
                <button
                  type="button"
                  class="checklist-menu-item is-secondary"
                  role="menuitem"
                  (click)="openSettings()">
                  <i class="fa-light fa-gear" aria-hidden="true"></i>
                  <span>{{
                    'ONBOARDING_CHECKLIST.MANAGE_IN_SETTINGS' | translate
                  }}</span>
                </button>
              </div>
            </div>
            <xds-icon-button
              *ngIf="!embedded"
              class="checklist-panel-close"
              size="sm"
              variant="ghost"
              icon="fa-light fa-xmark"
              [ariaLabel]="'CLOSE' | translate"
              (clicked)="toggle()"
            ></xds-icon-button>
          </div>
        </header>

        <!-- HEADER: konstanter Nenner ueber alle 6 Setup-Schritte. Ein
             Schritt aendert sich im Status, aber das Gesamtziel nicht.
             Sekundaer-Counter ("1 bereits eingerichtet" / "1 uebersprungen")
             sitzen NICHT hier — sie stehen ohnehin direkt im jeweiligen
             Section-Header weiter unten. Doppelte Information vermeiden.
             KEINE Restzeit-Anzeige hier (s. Header-Kommentar oben) — die
             ETA sitzt bewusst je Schritt in der Liste, s. checklist-item-sub. -->
        <div
          class="checklist-progress-block"
          [class.is-done]="allDone"
          [class.is-celebrating]="celebrating">
          <div class="checklist-progress-top">
            <span class="checklist-progress-label">
              {{
                'ONBOARDING_CHECKLIST.PROGRESS_LABEL'
                  | translate: { done: doneCount, total: totalAll }
              }}
            </span>
          </div>
          <div class="checklist-progress-bar-wrap">
            <!-- segments = sichtbare Etappen statt glattem Fill (Goal-Gradient-
                 Effekt, docs/design-proposals/setup-checklist-psychology-2026-07-21). -->
            <xds-progress
              [value]="progressPct"
              [segments]="totalAll"
              [tone]="allDone ? 'success' : 'primary'"
              [size]="allDone ? 'hairline' : 'default'"
              [subdued]="allDone"
            ></xds-progress>
            <!-- Sweep: One-Shot beim erstmaligen Erreichen von 6/6. -->
            <div
              *ngIf="celebrating"
              class="checklist-progress-sweep"
              aria-hidden="true"></div>
          </div>
        </div>

        <!-- SEKTION "Noch zu tun": offene Schritte (NeverSeen/Started-nicht-
             erledigt/Snoozed). Der naechste Schritt ist hervorgehoben. Jedes
             Item ist Whole-Row-Clickable (role=button + tabindex) und triggert
             dieselbe Aktion wie der CTA (Linear/Notion-Pattern). -->
        <h3
          *ngIf="openTours.length > 0"
          class="checklist-section-title">
          {{ 'ONBOARDING_CHECKLIST.SECTION_TODO_TITLE' | translate }}
        </h3>
        <ul class="checklist-list" *ngIf="openTours.length > 0">
          <li
            *ngFor="let t of openTours; trackBy: trackById; let i = index"
            class="checklist-item"
            role="button"
            tabindex="0"
            (click)="activate(t)"
            (keydown.enter)="activate(t)"
            (keydown.space)="$event.preventDefault(); activate(t)"
            [xdsTooltip]="t.isLocked ? lockTooltip(t) : (t.descKey | translate)"
            xdsTooltipPosition="top"
            xdsTooltipClass="xp-tour-tooltip"
            [xdsTooltipShowDelay]="350"
            [style.--cascade-delay.ms]="i * 80"
            [class.is-done]="t.status === 'completed' || (t.status === 'started' && t.wasEverCompleted)"
            [class.is-active]="t.status === 'started' && !t.wasEverCompleted && !t.isLocked"
            [class.is-replaying]="t.status === 'started' && t.wasEverCompleted"
            [class.is-next]="isPrimaryNext(t, i)"
            [class.is-locked]="t.isLocked"
            [class.is-celebrating-item]="celebrating">
            <div class="checklist-item-icon" aria-hidden="true">
              <!-- Icon-Cases muessen mutually exclusive sein: NgSwitch in
                   Angular rendert ALLE matching ngSwitchCase-Branches,
                   nicht nur den ersten. Ohne den expliziten Ausschluss
                   wasEverCompleted=false im "started"-Case wuerden bei
                   einem Replay zwei Icons gleichzeitig erscheinen
                   (Haekchen + halber Kreis).
                   Locked-Tours zeigen ein Lock-Icon und ueberstimmen alle
                   anderen Status-Indikatoren. -->
              <!-- Mutually exclusive Icon-Branches via *ngIf. NgSwitch wurde
                   entfernt, weil ngSwitch=true mit mehreren matching Cases
                   ALLE rendert - das fuehrte zu Lock+Halbmond-Doppel auf
                   gelockten Items. (Keine Backticks in Comments — die
                   beenden sonst das Component-Template-Literal!) -->
              <i
                *ngIf="t.isLocked"
                class="fa-solid fa-lock checklist-lock-icon"></i>
              <i
                *ngIf="!t.isLocked && t.status === 'completed'"
                class="fa-solid fa-circle-check"></i>
              <i
                *ngIf="!t.isLocked && t.status === 'started' && !!t.wasEverCompleted"
                class="fa-solid fa-circle-check"></i>
              <i
                *ngIf="!t.isLocked && t.status === 'started' && !t.wasEverCompleted"
                class="fa-solid fa-circle-half-stroke"></i>
              <i
                *ngIf="!t.isLocked && t.status !== 'completed' && t.status !== 'started'"
                class="fa-regular fa-circle"></i>
            </div>
            <div class="checklist-item-body">
              <div
                *ngIf="t.status === 'started' && t.wasEverCompleted && !t.isLocked"
                class="checklist-item-kicker is-replaying-kicker">
                {{ 'ONBOARDING_CHECKLIST.KICKER_REPLAYING' | translate }}
              </div>
              <div
                *ngIf="t.status === 'started' && !t.wasEverCompleted && !t.isLocked"
                class="checklist-item-kicker">
                {{ 'ONBOARDING_CHECKLIST.KICKER_ACTIVE' | translate }}
              </div>
              <div
                *ngIf="isPrimaryNext(t, i)"
                class="checklist-item-kicker">
                {{ 'ONBOARDING_CHECKLIST.KICKER_NEXT' | translate }}
              </div>
              <div
                *ngIf="t.isLocked"
                class="checklist-item-kicker is-locked-kicker">
                {{ 'ONBOARDING_CHECKLIST.KICKER_LOCKED' | translate }}
              </div>
              <div class="checklist-item-title">
                {{ t.titleKey | translate }}
              </div>
              <!-- Sub-Text: Schritt-Fortschritt (laufende Tour) UND/ODER ein
                   "~N Min."-Zeit-Hinweis aus TourConfig.estimatedDurationSec.
                   FRUEHER bewusst ohne Zeit-Chip ("mehr Lese-Last als
                   Mehrwert") — das Feld stand damals aber nirgends real
                   nutzbar zur Verfuegung. Jetzt sichtbar gemacht, weil
                   Unsicherheit ueber die Restdauer ein Hauptabbruchgrund bei
                   mehrstufigen Setups ist (docs/design-proposals/
                   setup-checklist-psychology-2026-07-21, Aufwandsklarheit).
                   Gesperrte Items bleiben ohne Zeit-Hinweis — nicht anwaehlbar,
                   ein "~N Min." wuerde falsche Unmittelbarkeit suggerieren. -->
              <div
                *ngIf="!t.isLocked && (t.stepProgress || t.estimatedDurationSec)"
                class="checklist-item-sub">
                <ng-container *ngIf="t.status === 'started' && t.stepProgress">
                  {{ 'ONBOARDING_CHECKLIST.STEP' | translate }}
                  {{ t.stepProgress.done }} /
                  {{ t.stepProgress.total }}
                  <ng-container *ngIf="t.estimatedDurationSec"> · </ng-container>
                </ng-container>
                <ng-container *ngIf="t.estimatedDurationSec">
                  {{
                    'ONBOARDING_CHECKLIST.STEP_MINUTES'
                      | translate: { minutes: stepMinutes(t) }
                  }}
                </ng-container>
              </div>
            </div>
            <div class="checklist-item-actions">
              <!-- Erklaervideo: zusaetzliches Angebot, KEIN zweiter CTA.
                   Deshalb Icon-Button statt Text-Button — der eine gefuellte
                   Knopf pro Zeile bleibt die Tour. Auch auf gesperrten Items
                   sichtbar: Zusehen setzt keine Voraussetzungen voraus, und
                   genau wer noch nicht darf, will oft erst verstehen. -->
              <xds-icon-button
                *ngIf="t.video"
                icon="fa-light fa-circle-play"
                size="sm"
                variant="ghost"
                class="checklist-item-video"
                [ariaLabel]="'ONBOARDING_CHECKLIST.WATCH_VIDEO' | translate"
                [title]="
                  'ONBOARDING_CHECKLIST.WATCH_VIDEO_HINT'
                    | translate: { minutes: t.video.durationMinutes }
                "
                (clicked)="$event.stopPropagation(); openVideo(t.video)">
              </xds-icon-button>
              <ng-container *ngIf="t.isLocked; else unlockedActions">
                <!-- Locked: kein interaktiver Button, nur ein dezentes Label.
                     Der Klick auf das Item-Surface (li-Element selbst) springt
                     bereits zum fehlenden Vorgaenger via activate(). -->
                <span class="checklist-item-locked-hint" aria-hidden="true">
                  <i class="fa-solid fa-lock"></i>
                </span>
              </ng-container>
              <ng-template #unlockedActions>
                <ng-container [ngSwitch]="t.status">
                  <xds-button
                    *ngSwitchCase="'completed'"
                    variant="ghost"
                    size="sm"
                    class="checklist-item-cta is-ghost"
                    (clicked)="$event.stopPropagation(); activate(t)">
                    {{
                      (allDone
                        ? 'ONBOARDING_CHECKLIST.VIEW_AGAIN'
                        : 'ONBOARDING_CHECKLIST.REPLAY') | translate
                    }}
                  </xds-button>
                  <xds-button
                    *ngSwitchCase="'started'"
                    variant="primary"
                    size="sm"
                    class="checklist-item-cta is-primary"
                    iconRight="fa-solid fa-arrow-right"
                    (clicked)="$event.stopPropagation(); activate(t)">
                    {{ 'ONBOARDING_CHECKLIST.RESUME' | translate }}
                  </xds-button>
                  <xds-button
                    *ngSwitchDefault
                    [variant]="isPrimaryNext(t, i) ? 'primary' : 'secondary'"
                    size="sm"
                    class="checklist-item-cta"
                    [class.is-primary]="isPrimaryNext(t, i)"
                    [iconRight]="isPrimaryNext(t, i) ? 'fa-solid fa-arrow-right' : undefined"
                    (clicked)="$event.stopPropagation(); activate(t)">
                    {{ 'ONBOARDING_CHECKLIST.START' | translate }}
                  </xds-button>
                </ng-container>
              </ng-template>
            </div>
          </li>
        </ul>

        <!-- SEKTION "Erledigt": ALLE abgeschlossenen Schritte einheitlich —
             egal ob selbst durchgespielt (completed) oder automatisch erkannt
             (auto-completed). EIN Look (gruener Haken), EINE Aktion („Ansehen").
             Loest die fruehere verwirrende Doppelung (gruenes „Nochmal" oben +
             separate „Bereits eingerichtet / Tour ansehen / Ueberspringen") auf
             (User-Wunsch 2026-06-17). Whole-Row-Clickable wie die Offen-Items. -->
        <section
          *ngIf="doneTours.length > 0"
          class="checklist-section is-done-section"
          aria-labelledby="done-section-title">
          <h3
            id="done-section-title"
            class="checklist-section-title">
            {{
              'ONBOARDING_CHECKLIST.SECTION_DONE_TITLE'
                | translate: { count: doneTours.length }
            }}
          </h3>
          <ul class="checklist-list">
            <li
              *ngFor="let t of doneTours; trackBy: trackById; let i = index"
              class="checklist-item is-done"
              role="button"
              tabindex="0"
              (click)="activate(t)"
              (keydown.enter)="activate(t)"
              (keydown.space)="$event.preventDefault(); activate(t)"
              [xdsTooltip]="t.descKey | translate"
              xdsTooltipPosition="top"
              xdsTooltipClass="xp-tour-tooltip"
              [xdsTooltipShowDelay]="350"
              [style.--cascade-delay.ms]="i * 60">
              <div class="checklist-item-icon" aria-hidden="true">
                <i class="fa-light fa-circle-check"></i>
              </div>
              <div class="checklist-item-body">
                <div class="checklist-item-title">
                  {{ t.titleKey | translate }}
                </div>
              </div>
              <div class="checklist-item-actions">
                <!-- Auch hier — und hier sogar am wichtigsten: Das Video zur
                     ersten Planung richtet sich an genau die, die den Mentor
                     schon hinter sich haben. Stünde der Knopf nur in der
                     Offen-Liste, verschwände er in dem Moment, in dem er
                     gebraucht wird. -->
                <xds-icon-button
                  *ngIf="t.video"
                  icon="fa-light fa-circle-play"
                  size="sm"
                  variant="ghost"
                  class="checklist-item-video"
                  [ariaLabel]="'ONBOARDING_CHECKLIST.WATCH_VIDEO' | translate"
                  [title]="
                    'ONBOARDING_CHECKLIST.WATCH_VIDEO_HINT'
                      | translate: { minutes: t.video.durationMinutes }
                  "
                  (clicked)="$event.stopPropagation(); openVideo(t.video)">
                </xds-icon-button>
                <xds-button
                  variant="ghost"
                  size="sm"
                  class="checklist-item-cta is-ghost"
                  (clicked)="$event.stopPropagation(); activate(t)">
                  {{ 'ONBOARDING_CHECKLIST.VIEW' | translate }}
                </xds-button>
              </div>
            </li>
          </ul>
        </section>

        <!-- SECTION "Übersprungen": collapsible Default — die Items
             sind dem User bewusst, der Header alleine reicht als
             Auffindbarkeit. Erst auf Wunsch klappt die Liste auf.
             User soll bewusst zugreifen muessen, nicht abgelenkt werden. -->
        <section
          *ngIf="dismissedTours.length > 0"
          class="checklist-secondary-section is-muted"
          aria-labelledby="dismissed-section-title">
          <button
            type="button"
            class="checklist-secondary-toggle"
            [attr.aria-expanded]="dismissedExpanded"
            aria-controls="dismissed-section-list"
            (click)="toggleDismissed()">
            <i
              class="fa-solid checklist-secondary-toggle-chevron"
              [class.fa-chevron-down]="dismissedExpanded"
              [class.fa-chevron-right]="!dismissedExpanded"
              aria-hidden="true"></i>
            <span
              id="dismissed-section-title"
              class="checklist-secondary-title is-muted-title">
              {{
                'ONBOARDING_CHECKLIST.SECTION_DISMISSED_TITLE'
                  | translate: { count: dismissedCount }
              }}
            </span>
          </button>
          <div
            *ngIf="dismissedExpanded"
            id="dismissed-section-list"
            class="checklist-secondary-collapse">
            <ul class="checklist-secondary-list">
              <li
                *ngFor="let t of dismissedTours; trackBy: trackById"
                class="checklist-secondary-item is-dismissed"
                [xdsTooltip]="t.descKey | translate"
                xdsTooltipPosition="top"
                xdsTooltipClass="xp-tour-tooltip"
                [xdsTooltipShowDelay]="350">
                <span class="checklist-secondary-item-title">
                  {{ t.titleKey | translate }}
                </span>
                <button
                  type="button"
                  class="checklist-secondary-link"
                  (click)="restore(t)">
                  {{ 'ONBOARDING_CHECKLIST.RESTORE' | translate }}
                </button>
              </li>
            </ul>
          </div>
        </section>

        <footer
          *ngIf="!allDone"
          class="checklist-panel-footer">
          <i class="fa-light fa-circle-info" aria-hidden="true"></i>
          <span>{{ 'ONBOARDING_CHECKLIST.FOOTER_HINT' | translate }}</span>
        </footer>
        <!-- Done-Footer: primaerer CTA fuhrt den User aktiv aus dem Setup-
             Modus in die naechste sinnvolle Aktion (Schichtplanung). Das
             Ausblenden liegt in der dauerhaft sichtbaren Verwalten-Zeile
             unten — so deckt EIN Einstieg alle Zustaende ab. -->
        <footer
          *ngIf="allDone"
          class="checklist-panel-footer is-done-footer">
          <button
            type="button"
            class="checklist-done-cta-primary"
            (click)="planFirstShift()">
            {{ 'ONBOARDING_CHECKLIST.PLAN_FIRST_SHIFT' | translate }}
            <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>
          </button>
        </footer>

      </aside>
    </ng-container>
  `,
  styles: [
    `
      :host {
        display: contents;
      }
      /* FAB — ruhig, aber klar lesbar als persistenter Einstieg. Leicht
         groesseres Padding, Font-Weight 600 statt 500 fuer Label-Prominenz,
         Primary-tinted Hintergrund-Hint an der Icon-Seite signalisiert
         "das ist der Setup-Eintrag, nicht irgendein FAB". Badge-Kontrast
         erhoeht. Kein Pulse-Effekt — Aufmerksamkeit uebernimmt der
         Coachmark beim ersten Besuch. */
      .checklist-fab {
        position: fixed;
        left: 18px;
        bottom: 18px;
        z-index: 8500;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        background: var(--clr-surface-100, #ffffff);
        color: var(--clr-text-100, #212529);
        border: 1px solid var(--clr-border, #d4dbe3);
        border-radius: 999px;
        padding: 10px 18px 10px 14px;
        font-size: 13.5px;
        font-weight: 600;
        letter-spacing: 0.01em;
        cursor: pointer;
        box-shadow:
          0 8px 20px rgba(15, 23, 42, 0.12),
          0 1px 2px rgba(15, 23, 42, 0.06);
        transition: box-shadow 0.18s, transform 0.08s, border-color 0.18s;
        font-family: inherit;
      }
      .checklist-fab::before {
        /* dezenter Primary-Tint-Spot hinter dem Icon — signalisiert
           "Einrichtungs-Einstieg", nicht irgendein System-FAB */
        content: '';
        position: absolute;
        left: 6px;
        top: 50%;
        transform: translateY(-50%);
        width: 28px;
        height: 28px;
        border-radius: 999px;
        background: color-mix(
          in srgb,
          var(--clr-primary-100, #4ccaf1) 14%,
          transparent
        );
        pointer-events: none;
      }
      .checklist-fab > * {
        position: relative;
      }
      .checklist-fab:hover {
        border-color: color-mix(
          in srgb,
          var(--clr-primary-100, #4ccaf1) 45%,
          var(--clr-border, #d4dbe3)
        );
        box-shadow:
          0 12px 28px rgba(15, 23, 42, 0.16),
          0 1px 2px rgba(15, 23, 42, 0.06);
      }
      .checklist-fab:active {
        transform: translateY(1px);
      }

      /* Done-Pill: kompakter, ohne Label und Counter. Nur ein Erfolgs-
         Haekchen-Icon. Position bleibt identisch (Muskelgedaechtnis), aber
         der Footprint ist deutlich kleiner. Border + Tint wechseln auf
         Erfolgs-Gruen, damit sofort lesbar wird "ich bin im Done-State,
         nicht im Setup". */
      .checklist-fab.is-done-pill {
        padding: 0;
        width: 40px;
        height: 40px;
        justify-content: center;
        gap: 0;
        font-size: 16px;
        color: var(--clr-success, #48bb78);
        border-color: color-mix(
          in srgb,
          var(--clr-success, #48bb78) 35%,
          var(--clr-border, #d4dbe3)
        );
      }
      .checklist-fab.is-done-pill::before {
        background: color-mix(
          in srgb,
          var(--clr-success, #48bb78) 14%,
          transparent
        );
        width: 32px;
        height: 32px;
        left: 4px;
      }
      .checklist-fab.is-done-pill:hover {
        border-color: color-mix(
          in srgb,
          var(--clr-success, #48bb78) 55%,
          var(--clr-border, #d4dbe3)
        );
      }
      :host-context(.theme-dark) .checklist-fab.is-done-pill {
        color: var(--clr-success, #48bb78);
      }

      /* One-Shot-Pulse: laeuft genau einmal, wenn der User die Tour ueber
         den X-Button pausiert hat. Ein dezenter Glow-Ring "atmet" am Rand,
         genug um das Auge zum FAB zu ziehen, ohne als Daueraufmerksamkeits-
         vampir zu wirken. Klasse wird per Timeout (~700 ms) wieder entfernt,
         damit der FAB danach wieder vollstaendig zur Ruhe kommt. */
      .checklist-fab.is-pulsing {
        animation: checklist-fab-pulse 0.7s cubic-bezier(0.22, 1, 0.36, 1);
      }
      @keyframes checklist-fab-pulse {
        0% {
          box-shadow:
            0 0 0 0
              color-mix(
                in srgb,
                var(--clr-primary-100, #4ccaf1) 55%,
                transparent
              ),
            0 8px 20px rgba(15, 23, 42, 0.12);
        }
        60% {
          box-shadow:
            0 0 0 14px
              color-mix(
                in srgb,
                var(--clr-primary-100, #4ccaf1) 0%,
                transparent
              ),
            0 8px 20px rgba(15, 23, 42, 0.16);
        }
        100% {
          box-shadow:
            0 0 0 0
              color-mix(
                in srgb,
                var(--clr-primary-100, #4ccaf1) 0%,
                transparent
              ),
            0 8px 20px rgba(15, 23, 42, 0.12);
        }
      }

      /* Pause-Toast — sitzt direkt ueber dem FAB, durch einen abwaerts
         zeigenden Pfeil visuell mit ihm verbunden ("hier weitermachen ↓").
         Bewusst ohne Schliessen-Button: das Toast verschwindet von selbst,
         und Klick auf den FAB dismissed sofort. Slide-up-Reveal aus 6 px
         Distanz, gemaesster Easing, weicher Schatten. Dunkler Tint statt
         Primary-Saturation, damit es als ruhiger Hinweis liest und nicht
         als Status-Banner. */
      .checklist-pause-toast {
        position: fixed;
        left: 18px;
        bottom: 70px;
        z-index: 8550;
        display: inline-flex;
        align-items: center;
        gap: 9px;
        padding: 10px 14px;
        background: var(--clr-surface-100, #ffffff);
        color: var(--clr-text-100, #212529);
        border: 1px solid var(--clr-border, #e6e8ec);
        border-radius: 999px;
        font-size: 12.5px;
        font-weight: 500;
        letter-spacing: -0.005em;
        box-shadow:
          0 8px 24px -4px rgba(15, 23, 42, 0.18),
          0 2px 4px rgba(15, 23, 42, 0.06);
        animation:
          checklist-pause-toast-in 0.24s cubic-bezier(0.22, 1, 0.36, 1),
          checklist-pause-toast-out 0.32s cubic-bezier(0.4, 0, 1, 1) 3.28s
            forwards;
        font-family: inherit;
        max-width: calc(100vw - 36px);
        pointer-events: none;
      }
      .checklist-pause-toast > i:first-child {
        color: var(--clr-primary-100, #4ccaf1);
        font-size: 14px;
      }
      .checklist-pause-toast-text {
        line-height: 1.3;
      }
      .checklist-pause-toast-arrow {
        font-size: 11px;
        color: var(--clr-text-200, #7a7a7a);
        margin-left: 2px;
        animation: checklist-pause-toast-arrow-bob 1.2s ease-in-out infinite;
      }
      @keyframes checklist-pause-toast-in {
        from {
          opacity: 0;
          transform: translateY(6px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes checklist-pause-toast-out {
        to {
          opacity: 0;
          transform: translateY(2px);
        }
      }
      @keyframes checklist-pause-toast-arrow-bob {
        0%,
        100% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(2px);
        }
      }
      /* Dark-Mode: Toast hebt sich auch auf dunklem Grund klar ab. */
      :host-context(.theme-dark) .checklist-pause-toast {
        background: #1e293b;
        color: #e2e8f0;
        border-color: #334155;
        box-shadow:
          0 8px 24px -4px rgba(0, 0, 0, 0.5),
          0 2px 4px rgba(0, 0, 0, 0.3);
      }
      :host-context(.theme-dark) .checklist-pause-toast-arrow {
        color: #94a3b8;
      }
      /* Reduced Motion: weder Pulse noch bobbing — Toast erscheint und
         verschwindet einfach mit Fade. */
      @media (prefers-reduced-motion: reduce) {
        .checklist-fab.is-pulsing {
          animation: none;
        }
        .checklist-pause-toast {
          animation:
            checklist-pause-toast-fade-in 0.18s ease-out,
            checklist-pause-toast-out 0.18s ease-out 3.28s forwards;
        }
        .checklist-pause-toast-arrow {
          animation: none;
        }
        @keyframes checklist-pause-toast-fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      }
      .checklist-fab i.fa-list-check {
        color: var(--clr-primary-100, #4ccaf1);
        font-size: 16px;
      }
      .checklist-fab-label {
        line-height: 1;
      }
      .checklist-fab-badge {
        display: inline-flex;
        min-width: 22px;
        height: 22px;
        padding: 0 7px;
        align-items: center;
        justify-content: center;
        background: var(--clr-primary-100, #4ccaf1);
        color: white;
        border-radius: 99px;
        font-size: 11.5px;
        font-weight: 700;
        line-height: 1;
        box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
      }
      /* ═══════════════════════ PANEL (Design-Refresh Session Teil 9) ═══════════════════════
         Moderne SaaS-Optik orientiert an Notion/Linear/Stripe. Weiche
         Schatten-Stack, mehr Luft, klarer „Current-Step"-Fokus, dezente
         Micro-Interactions. */
      .checklist-panel {
        position: fixed;
        left: 16px;
        bottom: 16px;
        z-index: 8600;
        width: 360px;
        max-width: calc(100vw - 32px);
        max-height: calc(100vh - 120px);
        overflow: hidden auto;
        /* Explizit opak — die globale --clr-surface-100 ist im Light-Mode
           94 % weiss (#fffffff0) und liess die Tabelle dahinter durchscheinen.
           Dark-Mode-Override unten setzt #212e3a (analog Variables-Dark). */
        background: #ffffff;
        color: var(--clr-text-100, #212529);
        border: 1px solid var(--clr-border, #e6e8ec);
        border-radius: 16px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.04),
          0 12px 24px -8px rgba(15, 23, 42, 0.12),
          0 24px 48px -12px rgba(15, 23, 42, 0.18);
        animation: checklist-fade-in 0.22s cubic-bezier(0.22, 1, 0.36, 1);
        font-family: inherit;
      }
      /* Eingebettet im Help-Hub-Tab: keine eigene Aussenhuelle/Position —
         der Hub liefert Rahmen, Schatten und Groesse. */
      .checklist-panel.embedded {
        position: static;
        left: auto;
        bottom: auto;
        z-index: auto;
        width: 100%;
        max-width: 100%;
        max-height: none;
        border: none;
        border-radius: 0;
        box-shadow: none;
        animation: none;
        background: transparent;
      }
      .checklist-panel.is-all-done.embedded {
        width: 100%;
      }
      /* Mobile: das Panel füllt via .hub-panel (help-hub.component.ts,
         @media max-width:600px) den GESAMTEN Bildschirm — die Schrift/Buttons
         waren aber unverändert auf Desktop-Popover-Größe fixiert (17px-Titel,
         Fortsetzen/Starten-Buttons über <xds-button>, das die globale Touch-
         Anhebung @pointer:coarse erbt). Gleiches Muster wie an jeder anderen
         Stelle dieser Mobile-Runde: zentrale Custom-Property-Overrides auf dem
         Panel-Root, die an alle Nachfahren (inkl. der xds-Buttons) vererben —
         User-Feedback 2026-07-22, "Schrift und Buttons ein bisschen
         überdimensional". */
      @media (max-width: 600px) {
        .checklist-panel.embedded {
          --control-sm: 32px;
          --control-md: 40px;
          --control-lg: 44px;
          --fs-base: 0.8125rem;
          --fs-sm: 0.75rem;
        }
        .checklist-panel.embedded .checklist-panel-title {
          font-size: 15px;
        }
        .checklist-panel.embedded .checklist-panel-header {
          padding: 16px 16px 10px;
        }
        .checklist-panel.embedded .checklist-progress-block {
          padding: 0 16px 12px;
        }
        .checklist-panel.embedded .checklist-list {
          padding: 4px 10px 8px;
        }
        .checklist-panel.embedded .checklist-item {
          padding: 10px;
        }
        .checklist-panel.embedded .checklist-item-icon {
          font-size: 16px;
        }
        .checklist-panel.embedded .checklist-panel-footer {
          padding: 12px 16px 16px;
        }
      }
      @keyframes checklist-fade-in {
        from {
          opacity: 0;
          transform: translateY(8px) scale(0.985);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      /* Header */
      .checklist-panel-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        padding: 20px 20px 12px;
      }
      .checklist-panel-heading {
        min-width: 0;
      }
      .checklist-panel-title {
        margin: 0;
        font-size: 17px;
        font-weight: 600;
        line-height: 1.3;
        letter-spacing: -0.005em;
      }
      /* Progress */
      .checklist-progress-block {
        padding: 0 20px 16px;
      }
      .checklist-progress-top {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 8px;
      }
      /* Primaere Progress-Aussage jetzt in Worten ("X von Y abgeschlossen")
         statt Prozent-dominant — konkreter + barrierefreundlicher. */
      .checklist-progress-label {
        font-size: 13px;
        font-weight: 600;
        color: var(--clr-text-100, #212529);
        letter-spacing: -0.005em;
        font-variant-numeric: tabular-nums;
      }
      .checklist-progress-pct {
        font-size: 11.5px;
        font-weight: 500;
        color: var(--clr-text-200, #7a7a7a);
        letter-spacing: 0.01em;
        font-variant-numeric: tabular-nums;
      }
      /* Fortschrittsbalken via xds-progress; Ton, Hairline und Dämpfung sind
         zentrale Inputs. Hier verbleibt nur der Positionierungsrahmen für den Sweep. */
      .checklist-progress-bar-wrap {
        position: relative;
      }
      .checklist-progress-bar-wrap xds-progress {
        width: 100%;
      }
      /* Sweep: One-Shot beim Erstmal-Erreichen von 6/6. Eine 40%-breite,
         halbtransparente Linie laeuft einmal von links nach rechts, fuegt
         sich in 0.6 s genau einmal ein und verschwindet wieder. Reine CSS-
         Animation, kein JS-Timer noetig (das *ngIf am Element steuert die
         Existenz). cubic-bezier-Ease bringt einen leichten "Glanz"-Effekt. */
      .checklist-progress-sweep {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 40%;
        left: 0;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.55),
          transparent
        );
        pointer-events: none;
        animation: checklist-progress-sweep 600ms cubic-bezier(0.4, 0, 0.2, 1) 1;
      }
      @keyframes checklist-progress-sweep {
        0% {
          transform: translateX(-100%);
          opacity: 0;
        }
        20% {
          opacity: 0.85;
        }
        100% {
          transform: translateX(220%);
          opacity: 0;
        }
      }

      /* Header-Crossfade fuer Done-State. Zwei Spans liegen uebereinander,
         Opacity-Toggle steuert den Wechsel ueber 400 ms. Statt display:none
         halten wir beide im DOM — sonst springt der Header beim ersten
         Erscheinen des Done-Titels. */
      .checklist-panel-title {
        position: relative;
      }
      .checklist-panel-title .title-default,
      .checklist-panel-title .title-done {
        display: block;
        transition: opacity 0.4s ease;
      }
      .checklist-panel-title .title-done {
        position: absolute;
        inset: 0;
        opacity: 0;
        pointer-events: none;
      }
      .checklist-panel-title .title-done-main {
        display: block;
      }
      .checklist-panel-title .title-done-date {
        display: block;
        margin-top: 2px;
        font-size: 11.5px;
        font-weight: 500;
        color: var(--clr-text-200, #7a7a7a);
        letter-spacing: 0;
      }
      .checklist-panel-title.is-crossfade .title-default {
        opacity: 0;
        pointer-events: none;
      }
      .checklist-panel-title.is-crossfade .title-done {
        position: static;
        opacity: 1;
        pointer-events: auto;
      }

      /* Haekchen-Kaskade: jedes Item-Icon pulst kurz. Per-Item-Delay kommt
         als CSS-Custom-Property aus dem Template (0, 80, 160, ...). Wirkt
         wie ein Build-Lauf — kontrolliert, nicht festlich. */
      @keyframes checklist-item-pulse {
        0%,
        100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.18);
        }
      }
      .checklist-item.is-celebrating-item .checklist-item-icon {
        animation: checklist-item-pulse 320ms ease-in-out
          var(--cascade-delay, 0ms) 1;
      }

      /* Done-Footer: ersetzt den Hint-Text durch eine klare Aktion +
         dezenten Hide-Link. Optisch klar getrennt vom regulaeren Footer
         (kein Info-Icon, mehr Padding, primaerer Button-Stil). */
      .checklist-panel-footer.is-done-footer {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
        padding: 14px 20px 18px;
      }
      /* Done-State-CTA bewusst RUHIG (User-Wunsch 2026-06-17): kein vollflaechiger
         cyan-Button — der wirkt im „abgeschlossen"-Panel wie ein offener Schritt.
         Stattdessen ein dezenter Outline-/Text-Link, der als sanfte Einladung
         („du bist fertig — jetzt loslegen") liest, ohne das ruhige Done-Panel zu
         dominieren. Farbe nur als Rand + Text (Farb-Diaet), gefuellt erst on-hover. */
      .checklist-done-cta-primary {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 8px 14px;
        background: transparent;
        color: var(--clr-primary-200, #3e95be);
        border: 1px solid
          color-mix(in srgb, var(--clr-primary-100, #4ccaf1) 40%, transparent);
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: -0.005em;
        cursor: pointer;
        font-family: inherit;
        box-shadow: none;
        transition:
          background-color 0.18s,
          border-color 0.18s,
          color 0.18s,
          transform 0.08s;
      }
      .checklist-done-cta-primary:hover {
        background: color-mix(
          in srgb,
          var(--clr-primary-100, #4ccaf1) 10%,
          transparent
        );
        border-color: color-mix(
          in srgb,
          var(--clr-primary-100, #4ccaf1) 60%,
          transparent
        );
        color: var(--clr-primary-200, #3e95be);
      }
      .checklist-done-cta-primary:focus-visible {
        outline: 2px solid
          color-mix(in srgb, var(--clr-primary-100, #4ccaf1) 60%, transparent);
        outline-offset: 2px;
      }
      .checklist-done-cta-primary:active {
        transform: translateY(1px);
      }
      .checklist-done-cta-primary i {
        font-size: 11px;
        opacity: 0.75;
      }
      :host-context(.theme-dark) .checklist-done-cta-primary {
        color: var(--clr-primary-100, #4ccaf1);
      }
      .checklist-done-cta-muted {
        display: inline-flex;
        justify-content: center;
        padding: 6px 10px;
        background: transparent;
        color: var(--clr-text-200, #7a7a7a);
        border: none;
        font-size: 12px;
        font-weight: 500;
        letter-spacing: 0;
        cursor: pointer;
        font-family: inherit;
        text-decoration: underline;
        text-decoration-color: transparent;
        text-underline-offset: 2px;
        transition:
          color 0.18s,
          text-decoration-color 0.18s;
      }
      .checklist-done-cta-muted:hover {
        color: var(--clr-text-100, #212529);
        text-decoration-color: currentColor;
      }
      .checklist-done-cta-muted:focus-visible {
        outline: 2px solid
          color-mix(in srgb, var(--clr-primary-100, #4ccaf1) 60%, transparent);
        outline-offset: 2px;
        border-radius: 4px;
      }

      /* ═══════════════════════ DONE-STATE-OPTIMIERUNG ═══════════════════════
         Wenn die Bubble im Done-State (alle 7 Touren mind. einmal echt
         abgeschlossen) geoeffnet wird, soll das Panel deutlich ruhiger und
         kompakter wirken — Stripe/Linear/Notion-Tonfall:
         - Panel etwas schmaler (340px statt 360px)
         - Subzeile "X von Y erledigt" weg (Header sagt es schon)
         - Progress-Bar zur 2px-Hairline reduziert, gedeckter Gruenton
         - Items kompakter (~36px Hoehe statt ~56px)
         - "Anschauen"-Buttons verschwinden im Idle, erscheinen on-hover
         - Footer-Border-Top wegfallen, CTA gewinnt visuelle Hierarchie */

      .checklist-panel.is-all-done {
        width: 340px;
      }

      /* Subzeile "X von Y" verstecken — Header + relatives Datum sagen es. */
      .checklist-panel.is-all-done .checklist-progress-top {
        display: none;
      }
      /* Hairline statt Balken — 2px, ohne Inset-Glanz, gedecktes Gruen.
         Bleibt als visueller Status-Indikator stehen, ohne Aufmerksamkeit
         zu binden. Padding um den Block reduziert (kein doppelter Spacer). */
      .checklist-panel.is-all-done .checklist-progress-block {
        padding: 0 20px 12px;
      }

      /* Header-Icon: dezenter Erfolgs-Kreis links neben dem Done-Titel.
         Inline-Variante (statt absolutes Positionieren), damit der Icon
         mit dem Text zusammen mittig sitzt. */
      .checklist-panel-title .title-done-main {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-weight: 600;
      }
      .checklist-panel-title .title-done-icon {
        font-size: 16px;
        color: var(--clr-success, #48bb78);
        opacity: 0.85;
      }
      .checklist-panel-title .title-done-date {
        display: block;
        margin-top: 2px;
        font-size: 11.5px;
        font-weight: 400;
        color: var(--clr-text-200, #7a7a7a);
        letter-spacing: 0;
        cursor: default;
      }

      /* Liste kompakter: Padding und Gap reduziert, Item-Padding kuerzer.
         "Anschauen"-Buttons verschwinden im Idle (opacity 0). Item ist
         als Ganzes klickbar (bereits implementiert), die Aktion auf dem
         Button ist redundant — der Button ist nur Visual-Affordance.
         Auf Hover oder Keyboard-Focus blendet er sanft ein. */
      .checklist-panel.is-all-done .checklist-list {
        padding: 4px 12px 6px;
        gap: 0;
      }
      .checklist-panel.is-all-done .checklist-item {
        padding: 6px 12px;
        min-height: 32px;
      }
      .checklist-panel.is-all-done .checklist-item-cta.is-ghost {
        opacity: 0;
        transition: opacity 0.18s ease;
      }
      .checklist-panel.is-all-done .checklist-item:hover
        .checklist-item-cta.is-ghost,
      .checklist-panel.is-all-done .checklist-item:focus-within
        .checklist-item-cta.is-ghost,
      .checklist-panel.is-all-done .checklist-item-cta.is-ghost:focus-within {
        opacity: 1;
      }

      /* Footer im Done-State ohne Trenner — die kompakte Liste laeuft
         visuell in den CTA hinein, der dadurch dominanter wirkt. */
      .checklist-panel.is-all-done .checklist-panel-footer.is-done-footer {
        border-top: none;
        margin-top: 0;
      }

      /* List — etwas mehr Luft zwischen den Items, damit erledigt/aktiv
         visuell klarer getrennt wirkt. */
      .checklist-list {
        list-style: none;
        margin: 0;
        padding: 4px 12px 8px;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .checklist-item {
        display: grid;
        grid-template-columns: 24px 1fr auto;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border-radius: 10px;
        cursor: pointer;
        transition: background 0.14s ease, opacity 0.18s ease;
      }
      .checklist-item:hover {
        background: color-mix(
          in srgb,
          var(--clr-text-100, #212529) 4%,
          transparent
        );
      }
      /* Tastatur-Fokus auf der Row — wichtig, weil <li role="button">
         ueber Tab erreichbar ist. Ring matcht den CTA-Fokus, damit die
         Anwendung visuell konsistent bleibt. */
      .checklist-item:focus-visible {
        outline: 2px solid
          color-mix(in srgb, var(--action-primary, #0369a1) 60%, transparent);
        outline-offset: -2px;
      }
      /* Kleiner Extra-Abstand oberhalb des aktiven/naechsten Schritts,
         damit der „Jetzt dran"-Block klar vom „Erledigt"-Cluster absetzt. */
      .checklist-item.is-next,
      .checklist-item.is-active {
        margin-top: 4px;
      }
      /* --action-primary statt --clr-primary-100 (Cyan): der CTA in dieser
         Zeile ist ein echter xds-button[variant=primary] und rendert IMMER
         in --action-primary — Kicker/Icon/Tint dieser Zeile matchen jetzt
         dieselbe Farbfamilie statt sichtbar daneben abzuweichen. */
      .checklist-item.is-next {
        background: color-mix(
          in srgb,
          var(--action-primary, #0369a1) 7%,
          transparent
        );
      }
      .checklist-item.is-active {
        background: color-mix(
          in srgb,
          var(--action-primary, #0369a1) 10%,
          transparent
        );
      }
      /* Erledigte Items: zusaetzlich Gesamt-Deckkraft runter, damit die
         Aufmerksamkeit klar am aktiven/naechsten Schritt bleibt. */
      .checklist-item.is-done {
        opacity: 0.72;
      }
      .checklist-item.is-done:hover {
        opacity: 1;
      }

      /* Locked-Items: Voraussetzungs-Lock im Goldpfad. Sichtbar als Plan,
         aber dezent — der User soll nicht den Eindruck bekommen, da gaebe
         es einen aktiven CTA. Lock-Icon links + reduzierte Opazitaet +
         neutrale Hintergrund-Tint. Klick auf das Item leitet via activate()
         zum fehlenden Vorgaenger weiter.
         Kompakte Padding-Variante: gelockte Items tragen weniger Inhalt
         (kein Sub-Step, kein primaerer CTA), brauchen also auch weniger
         vertikalen Raum. So wirkt die Liste insgesamt kuerzer und das
         Auge bleibt am aktiven Schritt haengen. */
      .checklist-item.is-locked {
        opacity: 0.55;
        /* Cursor bleibt 'pointer': das Item IST anklickbar — es leitet
           freundlich zum fehlenden Vorgaenger weiter. 'help' (Pfeil mit
           Fragezeichen) hat User irritiert, weil es nach reinem Tooltip-
           Hilfe-Element aussah. Tooltip uebernimmt die Erklaerung. */
        cursor: pointer;
        padding: 7px 12px; /* enger als das 12px-Default-Padding */
        background: transparent;
        border-color: transparent;
      }
      .checklist-item.is-locked:hover {
        opacity: 0.78;
        background: color-mix(
          in srgb,
          var(--clr-text-200, #7a7a7a) 5%,
          transparent
        );
      }
      /* Locked-Item: rechte Lock-Hint kompakter, kein 32x32-Knopf-Look mehr. */
      .checklist-item.is-locked .checklist-item-locked-hint {
        width: 20px;
        height: 20px;
        font-size: 12px;
      }
      /* Locked-Item: Kicker enger an den Titel ruecken, kein Punkt-Marker. */
      .checklist-item.is-locked .checklist-item-kicker.is-locked-kicker {
        font-size: 10px;
        margin-bottom: 1px;
      }
      .checklist-item.is-locked .checklist-item-kicker.is-locked-kicker::before {
        display: none; /* der Punkt-Indikator gehoert dem aktiven CTA */
      }
      /* Lock-Icon im Item-Bullet */
      .checklist-item.is-locked .checklist-item-icon {
        color: var(--clr-text-200, #7a7a7a);
      }
      .checklist-item .checklist-lock-icon {
        font-size: 14px;
      }
      /* "Gesperrt"-Kicker: visuell kleiner und neutraler als ALS NÄCHSTES /
         JETZT DRAN — soll keine Konkurrenz zum primaeren CTA bilden. */
      .checklist-item-kicker.is-locked-kicker {
        color: var(--clr-text-200, #7a7a7a);
        font-weight: 500;
        letter-spacing: 0.02em;
        opacity: 0.85;
      }
      .checklist-item-kicker.is-locked-kicker::before {
        background: var(--clr-text-200, #7a7a7a);
        opacity: 0.7;
      }
      /* Right-side hint statt Button — nur das Lock-Glyph reicht, der
         Tooltip auf dem Item erklaert die Begruendung. */
      .checklist-item-locked-hint {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        color: var(--clr-text-200, #7a7a7a);
        opacity: 0.7;
        font-size: 13px;
      }

      /* Icon */
      .checklist-item-icon {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        color: color-mix(
          in srgb,
          var(--clr-text-200, #7a7a7a) 55%,
          transparent
        );
        transition: color 0.18s, transform 0.18s;
      }
      /* WARUM (2026-07-21): vorher fa-solid + kraeftiges #10b981 — wirkte neben
         dem bereits gedaempften Titel/"Ansehen" (siehe .checklist-item.is-done
         weiter unten, opacity) visuell zu laut und zog in der Erledigt-Liste
         mehr Aufmerksamkeit auf sich als die "Jetzt dran"-Karte oben. Duennere
         fa-light-Kontur + kleinere Groesse + der (bereits gedaempftere)
         --action-success-Token statt hartkodiertem Neongruen machen erledigte
         Zeilen konsistent ruhig, statt nur Text+Deckkraft zu daempfen und das
         Icon knallig zu lassen. */
      .checklist-item.is-done .checklist-item-icon {
        color: var(--action-success, #047857);
        font-size: 15px;
        animation: check-pop 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
      /* Auto-detected: Daten existieren, aber Tour nie durchlaufen.
         Amber/Gold statt Gruen, damit der Unterschied auf einen Blick
         erkennbar ist. Cursor-Tooltip via [title] auf dem Icon erklaert es. */
      .checklist-item.is-auto .checklist-item-icon,
      .auto-completed-icon {
        color: #f59e0b;
      }
      .checklist-item-kicker.is-auto-kicker {
        color: #b45309;
      }
      .checklist-item-kicker.is-auto-kicker::before {
        background-color: #f59e0b;
      }

      /* Replay-Run: Tour wurde mind. einmal echt durchlaufen, laeuft gerade
         Wiederholung. Gruenes Haekchen bleibt (User hat es ja schon mal
         gemacht), aber Kicker zeigt "Wiederholung". */
      .checklist-item.is-replaying .checklist-item-icon {
        color: #10b981;
      }
      .checklist-item-kicker.is-replaying-kicker {
        color: #047857;
      }
      .checklist-item-kicker.is-replaying-kicker::before {
        background-color: #10b981;
      }

      /* Dismissed: User hat aktiv "Ueberspringen" geklickt. Grauer/dezenter
         Stil — sichtbar bleibt das Item, aber visuell als "deprio" markiert. */
      .checklist-item.is-dismissed {
        opacity: 0.55;
      }
      .checklist-item.is-dismissed .checklist-item-icon,
      .dismissed-icon {
        color: #9ca3af;
      }
      .checklist-item.is-dismissed .checklist-item-title {
        text-decoration: line-through;
        text-decoration-color: #d1d5db;
        color: #6b7280;
      }
      .checklist-item-kicker.is-dismissed-kicker {
        color: #6b7280;
      }
      .checklist-item-kicker.is-dismissed-kicker::before {
        background-color: #9ca3af;
      }
      .checklist-item.is-dismissed:hover {
        opacity: 0.85;
      }
      .checklist-progress-dismissed-hint {
        color: #6b7280;
        font-size: 12px;
        margin-left: 4px;
      }
      .checklist-item.is-active .checklist-item-icon,
      .checklist-item.is-next .checklist-item-icon {
        color: var(--action-primary, #0369a1);
      }
      @keyframes check-pop {
        0% {
          transform: scale(0.7);
          opacity: 0.4;
        }
        60% {
          transform: scale(1.12);
        }
        100% {
          transform: scale(1);
          opacity: 1;
        }
      }

      /* Body */
      .checklist-item-body {
        min-width: 0;
      }
      /* Kicker-Label ("Als Naechstes" / "Jetzt dran") — dezenter Primary-
         Eyebrow ueber dem Titel, der sofort zeigt, wohin der Blick gehoert.
         Mit Micro-Dot vorne, analog zu Linear/Notion-Kicker-Labels.
         --action-primary statt --clr-primary-100: matcht den echten
         xds-button[variant=primary]-CTA direkt in derselben Zeile. */
      .checklist-item-kicker {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 10.5px;
        font-weight: 600;
        letter-spacing: 0.07em;
        text-transform: uppercase;
        color: var(--action-primary, #0369a1);
        margin-bottom: 3px;
        line-height: 1;
      }
      .checklist-item-kicker::before {
        content: '';
        width: 5px;
        height: 5px;
        border-radius: 99px;
        background: currentColor;
        box-shadow: 0 0 0 3px
          color-mix(in srgb, currentColor 22%, transparent);
      }
      .checklist-item-title {
        font-size: 13.5px;
        font-weight: 500;
        color: var(--clr-text-100, #212529);
        line-height: 1.4;
        letter-spacing: -0.003em;
      }
      .checklist-item.is-next .checklist-item-title,
      .checklist-item.is-active .checklist-item-title {
        font-weight: 600;
      }
      .checklist-item.is-done .checklist-item-title {
        color: var(--clr-text-200, #7a7a7a);
        font-weight: 400;
        /* kein line-through — reduziert visuellen Laerm, Icon + Farbe
           signalisieren "erledigt" eindeutig */
      }
      .checklist-item-sub {
        font-size: 12px;
        color: var(--clr-text-200, #7a7a7a);
        margin-top: 3px;
        line-height: 1.45;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* SECONDARY SECTIONS ("Bereits eingerichtet", "Übersprungen")
         Optisch klar abgesetzt vom Goldpfad oben. Kompaktere Items,
         dezenterer Stil, Text-Links statt Buttons. Gruen ist hier
         bewusst NICHT mehr im Section-Header — diese Farbe ist den
         echten Goldpfad-Erfolgen vorbehalten. */
      .checklist-secondary-section {
        margin: 8px 18px 0;
        padding-top: 12px;
        border-top: 1px solid var(--clr-border, #e6e8ec);
      }
      .checklist-secondary-section.is-muted {
        opacity: 0.85;
      }
      .checklist-secondary-title {
        margin: 0 0 4px;
        padding: 0;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--clr-text-200, #7a7a7a);
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      /* Auto-Section: dezenter Amber-Ton (nur Icon-Akzent), Text bleibt
         neutral. Signalisiert "Daten sind da" ohne mit dem Goldpfad-Gruen
         zu konkurrieren. */
      .checklist-secondary-title.is-auto-title {
        color: var(--clr-text-200, #7a7a7a);
      }
      .checklist-secondary-title.is-auto-title i {
        color: #b45309;
      }
      .checklist-secondary-title.is-muted-title {
        color: #9ca3af;
      }
      .checklist-secondary-title i {
        font-size: 12px;
      }
      /* Sektions-Header der einheitlichen Liste ("Noch zu tun" / "Erledigt").
         Kleiner, ruhiger Uppercase-Trenner — strukturiert die Liste, ohne mit
         den Items zu konkurrieren (dezente Gamification: klare Trennung von
         offen vs. erreicht). */
      .checklist-section-title {
        margin: 12px 2px 6px;
        padding: 0;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--clr-text-200, #7a7a7a);
      }
      /* "Erledigt"-Sektion leicht absetzen; die Items sind via .is-done
         ohnehin optisch beruhigt (gedaempfter Haken, Ghost-Aktion). */
      .checklist-section.is-done-section {
        margin-top: 2px;
      }
      /* Toggle-Button fuer die kollabierte "Übersprungen"-Section.
         Sieht nicht wie ein Button aus — er IST der Section-Header.
         Komplette Klickflaeche, ruhiger Hover-Tint. */
      .checklist-secondary-toggle {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 4px 6px;
        margin: 0 0 4px -6px;
        background: transparent;
        border: none;
        cursor: pointer;
        border-radius: 6px;
        font-family: inherit;
        text-align: left;
        transition: background 0.12s;
      }
      .checklist-secondary-toggle:hover {
        background: color-mix(
          in srgb,
          var(--clr-text-100, #212529) 5%,
          transparent
        );
      }
      .checklist-secondary-toggle:focus-visible {
        outline: 2px solid
          color-mix(in srgb, var(--clr-primary-100, #4ccaf1) 60%, transparent);
        outline-offset: 2px;
      }
      .checklist-secondary-toggle-chevron {
        font-size: 9px;
        color: var(--clr-text-200, #7a7a7a);
        width: 10px;
        text-align: center;
        transition: transform 0.16s;
      }
      .checklist-secondary-collapse {
        animation: checklist-collapse-in 0.18s ease-out;
      }
      @keyframes checklist-collapse-in {
        from {
          opacity: 0;
          transform: translateY(-2px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .checklist-secondary-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .checklist-secondary-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 6px 4px;
        font-size: 13px;
      }
      /* Auto-Item: kleiner Aktions-Cluster rechts, "Tour ansehen" als
         primaerer Text-Link, "Überspringen" daneben deutlich dezenter
         (kleinere Schrift, gedeckter Ton). Reduziert das Optimierungs-
         angebot zum Wegklicken. */
      .checklist-secondary-actions {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        flex-shrink: 0;
      }
      .checklist-secondary-item-title {
        flex: 1;
        color: var(--clr-text-100, #212529);
        font-weight: 500;
      }
      .checklist-secondary-item.is-dismissed .checklist-secondary-item-title {
        color: #6b7280;
        text-decoration: line-through;
        text-decoration-color: #d1d5db;
      }
      .checklist-secondary-link {
        background: transparent;
        border: none;
        padding: 4px 8px;
        font-size: 12px;
        font-weight: 500;
        color: var(--clr-primary-100, #4ccaf1);
        cursor: pointer;
        font-family: inherit;
        border-radius: 6px;
        transition: background 0.14s, color 0.14s;
      }
      .checklist-secondary-link:hover {
        background: color-mix(in srgb, var(--clr-primary-100, #4ccaf1) 8%, transparent);
      }
      .checklist-secondary-link.is-skip {
        color: #6b7280;
        font-size: 11px;
      }
      .checklist-secondary-link.is-skip:hover {
        color: #4b5563;
        background: rgba(0, 0, 0, 0.04);
      }
      .checklist-secondary-link:focus-visible {
        outline: 2px solid
          color-mix(in srgb, var(--clr-primary-100, #4ccaf1) 60%, transparent);
        outline-offset: 1px;
      }

      /* Action-Container — bei AutoCompleted nimmt er zwei Buttons auf */
      .checklist-item-actions {
        display: flex;
        flex-direction: column;
        gap: 4px;
        align-items: stretch;
        flex-shrink: 0;
      }
      .checklist-item-actions .checklist-item-cta {
        width: 100%;
        justify-content: center;
      }
      .checklist-item-actions .checklist-item-cta.is-skip {
        font-size: 11px;
        padding: 4px 12px;
        opacity: 0.6;
      }
      .checklist-item-actions .checklist-item-cta.is-skip:hover {
        opacity: 1;
      }

      /* PROGRESS_AUTO_HINT — dezenter zusatz-Hint im Header */
      .checklist-progress-auto-hint {
        color: #b45309;
        font-size: 12px;
        margin-left: 4px;
      }

      /* CTA — Optik kommt jetzt von xds-button (Varianten primary/secondary/
         ghost); hier nur noch die Progressive-Disclosure-Choreografie
         (Grund-Deckkraft, Reveal bei Zeilen-Hover/Fokus). opacity wirkt
         trotz Kind-Komponente durchgaengig (kein ::ng-deep noetig, da Angulars
         Emulated Encapsulation kein echtes Shadow-DOM erzeugt). */
      .checklist-item-cta {
        opacity: 0.72;
        transition: opacity 0.14s;
      }
      .checklist-item:hover .checklist-item-cta,
      .checklist-item-cta:focus-within {
        opacity: 1;
      }
      /* Touch-Geraete haben keinen Hover-Status — der Sekundaer-CTA wuerde
         sonst dauerhaft auf 0.72 dimmen und wie deaktiviert wirken. Auf
         hover-losen Pointern setzen wir die volle Sichtbarkeit als Default. */
      @media (hover: none) {
        .checklist-item-cta {
          opacity: 1;
        }
        .checklist-item-cta.is-ghost {
          opacity: 0.85;
        }
      }
      /* Ghost-CTA (Nochmal/Ansehen) — extra zurueckhaltend, da er zu
         erledigten Items gehoert: niedrigere Grund-Deckkraft, kommt erst bei
         Hover/Fokus voll hervor. */
      .checklist-item-cta.is-ghost {
        opacity: 0.55;
      }
      .checklist-item.is-done:hover .checklist-item-cta.is-ghost,
      .checklist-item-cta.is-ghost:focus-within {
        opacity: 1;
      }

      /* Footer */
      .checklist-panel-footer {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        padding: 14px 20px 18px;
        font-size: 11.5px;
        line-height: 1.5;
        color: var(--clr-text-200, #7a7a7a);
        border-top: 1px solid
          color-mix(in srgb, var(--clr-border, #e6e8ec) 80%, transparent);
        margin-top: 8px;
      }
      .checklist-panel-footer i {
        font-size: 13px;
        margin-top: 1px;
        opacity: 0.7;
      }

      /* ═══════════════ Header-Aktionen: ⋮-Menue + Close ═══════════════
         Kebab + Close liegen rechts im Header. Das ⋮-Menue ersetzt den
         frueheren Footer-"Ausblenden"-Button: oben, sofort sichtbar, klar
         auf die "Setup-Checkliste" bezogen. */
      .checklist-panel-header-actions {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        flex-shrink: 0;
      }
      .checklist-menu-wrap {
        position: relative;
        display: inline-flex;
      }
      .checklist-panel-kebab {
        background: transparent;
        border: none;
        color: var(--clr-text-200, #7a7a7a);
        cursor: pointer;
        padding: 6px;
        border-radius: 8px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        font-size: 15px;
        transition: background 0.12s, color 0.12s;
      }
      .checklist-panel-kebab:hover,
      .checklist-panel-kebab.is-open {
        background: color-mix(in srgb, var(--clr-text-100, #212529) 6%, transparent);
        color: var(--clr-text-100, #212529);
      }
      .checklist-panel-kebab:focus-visible {
        outline: 2px solid
          color-mix(in srgb, var(--clr-primary-100, #4ccaf1) 60%, transparent);
        outline-offset: 2px;
      }
      /* Dropdown: rechtsbuendig unter dem Kebab. Eigener Stacking-Context
         ueber dem Panel-Inhalt; weiche Schatten-Stack analog zum Panel. */
      .checklist-menu {
        position: absolute;
        top: calc(100% + 6px);
        right: 0;
        z-index: 20;
        min-width: 232px;
        padding: 6px;
        background: #ffffff;
        border: 1px solid var(--clr-border, #e6e8ec);
        border-radius: 12px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.04),
          0 8px 20px -6px rgba(15, 23, 42, 0.16),
          0 16px 32px -12px rgba(15, 23, 42, 0.2);
        animation: checklist-menu-in 0.16s cubic-bezier(0.22, 1, 0.36, 1);
      }
      @keyframes checklist-menu-in {
        from {
          opacity: 0;
          transform: translateY(-4px) scale(0.98);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      .checklist-menu-item {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 9px 10px;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: var(--clr-text-100, #212529);
        font-family: inherit;
        font-size: 13px;
        font-weight: 600;
        text-align: left;
        cursor: pointer;
        transition: background 0.12s, color 0.12s;
      }
      .checklist-menu-item > i {
        font-size: 14px;
        width: 16px;
        text-align: center;
        opacity: 0.85;
      }
      .checklist-menu-item:hover {
        background: color-mix(
          in srgb,
          var(--clr-danger, #e25555) 8%,
          transparent
        );
        color: var(--clr-danger, #e25555);
      }
      /* Sekundaer-Eintrag "In Einstellungen verwalten": neutraler, kleiner,
         vom destruktiven Hide klar abgesetzt (eigene Hover-Tint). */
      .checklist-menu-item.is-secondary {
        font-weight: 500;
        color: var(--clr-text-200, #7a7a7a);
      }
      .checklist-menu-item.is-secondary:hover {
        background: color-mix(in srgb, var(--clr-text-100, #212529) 5%, transparent);
        color: var(--clr-text-100, #212529);
      }
      .checklist-menu-item:focus-visible {
        outline: 2px solid
          color-mix(in srgb, var(--clr-primary-100, #4ccaf1) 60%, transparent);
        outline-offset: -2px;
      }
      :host-context(.theme-dark) .checklist-menu {
        background: #1e293b;
        border-color: #334155;
        box-shadow:
          0 8px 24px -4px rgba(0, 0, 0, 0.55),
          0 2px 4px rgba(0, 0, 0, 0.35);
      }

      /* Dark-Mode Tweaks — Tokens greifen automatisch; hier nur Overrides
         fuer Shadow-Intensitaet und Tint-Deckkraft, damit der Hellblau-
         Tint auf dunklem Grund sichtbar bleibt. */
      :host-context(.theme-dark) .checklist-panel {
        background: #212e3a;
        box-shadow:
          0 1px 2px rgba(0, 0, 0, 0.3),
          0 12px 24px -8px rgba(0, 0, 0, 0.5),
          0 24px 48px -12px rgba(0, 0, 0, 0.6);
      }
      :host-context(.theme-dark) .checklist-item.is-next {
        background: color-mix(
          in srgb,
          var(--clr-primary-100, #4ccaf1) 14%,
          transparent
        );
      }
      :host-context(.theme-dark) .checklist-item.is-active {
        background: color-mix(
          in srgb,
          var(--clr-primary-100, #4ccaf1) 18%,
          transparent
        );
      }
    `,
  ],
})
export class OnboardingChecklistFabComponent implements OnInit, OnDestroy {
  visible = false;
  open = false;

  /**
   * Eingebettet-Modus (im Help-Hub als Tab): blendet den eigenen FAB-Launcher
   * + Resume-Toast + Close-Button aus und rendert das Panel inline (statt
   * fixed unten links). Der Hub uebernimmt Launcher/Sichtbarkeit. Beim Setzen
   * wird das Panel dauerhaft geoeffnet (open=true), damit der View-Modus den
   * normalen, aufgeklappten Inhalt zeigt.
   */
  private _embedded = false;
  @Input() set embedded(value: boolean) {
    this._embedded = value;
    if (value) {
      this.open = true;
    }
  }
  get embedded(): boolean {
    return this._embedded;
  }
  /** Vollstaendige Liste aller Touren (alle Status). Bleibt fuer Counter
   *  und interne Logik notwendig. */
  tours: Array<{
    id: TourFeature;
    titleKey: string;
    descKey: string;
    status: TourStateStatus;
    stepProgress?: { done: number; total: number };
    /** True wenn die Tour mindestens einmal echt durchgespielt wurde
     *  (completedAt ist gesetzt). Erlaubt im Template, eine laufende
     *  Replay-Runde von einem Erst-Durchlauf zu unterscheiden. */
    wasEverCompleted?: boolean;
    /** Roher Completion-Zeitpunkt aus dem Snapshot. Bleibt ueber
     *  transitionReplay() hinweg erhalten. Quelle fuer den
     *  "Abgeschlossen am ..."-Datumsstempel im Done-Header. */
    completedAt?: Date;
    /** Voraussetzungs-Lock: solange `true`, sind alle Vorgaenger im Goldpfad
     *  noch nicht abgeschlossen. Item rendert dezent (Lock-Icon, opacity,
     *  kein primaerer Button) und Klick zeigt einen freundlichen Hinweis. */
    isLocked?: boolean;
    /** i18n-Keys der noch ausstehenden Vorgaenger-Touren — fuer den
     *  "Erst X & Y abschliessen"-Tooltip auf gelockten Items. */
    lockReasonTitleKeys?: string[];
    /** Rohe Zeitschaetzung der Tour aus der TourRegistry (Sekunden). Quelle fuer
     *  den "~N Min."-Hinweis je Schritt (docs/design-proposals/
     *  setup-checklist-psychology-2026-07-21) — Aufwandsklarheit gegen
     *  Abbruch bei mehrstufigen Setups. */
    estimatedDurationSec?: number;
    /** Erklaervideo zu diesem Schritt, falls hinterlegt UND schon hochgeladen
     *  (siehe `config/help-video.config.ts`). Rein zusaetzliches Angebot — der
     *  Erledigt-Status haengt weiterhin allein an der Tour. */
    video?: HelpVideo;
  }> = [];

  /**
   * Hauptliste = der "Goldpfad". Enthaelt nur Touren, die der User
   * aktiv lernen soll oder schon gelernt hat: NeverSeen, Seen, Started,
   * Completed. AutoCompleted und Dismissed wandern in eigene Sections,
   * damit sie den primaeren Lern-Pfad nicht visuell stoeren.
   */
  primaryTours: typeof this.tours = [];

  /**
   * Einheitliche Aufteilung (2026-06-17, User-Wunsch): NUR nach Status —
   * offen vs. erledigt. „Erledigt" fasst selbst-durchgespielte (completed) UND
   * automatisch erkannte (auto-completed) Touren zusammen, mit identischem
   * Look + EINER Aktion („Ansehen"). So gibt es keine verwirrende
   * Doppel-Darstellung (gruenes „Nochmal" oben vs. „Bereits eingerichtet /
   * Tour ansehen / Ueberspringen" unten) mehr.
   */
  openTours: typeof this.tours = [];
  doneTours: typeof this.tours = [];

  /** Section "Bereits eingerichtet": auto-detected Touren als kompakte Badges. */
  autoCompletedTours: typeof this.tours = [];

  /** Section "Übersprungen": vom User aktiv weggeklickte Touren, sehr dezent. */
  dismissedTours: typeof this.tours = [];
  /**
   * Im Header gezeigte Hauptzahl: alle Setup-Schritte, deren Ziel erreicht ist
   * (selbst durchgespielte Tour ODER Daten bereits vorhanden = auto-completed).
   * Skipped zaehlt nicht — der User hat aktiv entschieden, den Schritt nicht
   * zu lernen. Skipped wird stattdessen als sekundaerer Hint angezeigt.
   */
  doneCount = 0;
  /**
   * Konstanter Nenner: alle 6 fachlichen Setup-Schritte. Dauerhaft stabil,
   * unabhaengig davon ob ein Schritt auto-completed oder dismissed ist —
   * der User soll nie das Gefuehl haben, dass das Ziel sich verschiebt.
   */
  totalAll = 0;
  /** Touren, die der Detector als auto-completed markiert hat (Daten da,
   *  Tour nicht selbst gemacht). Bekommen eine eigene Section. */
  autoCount = 0;
  /** Touren, die der User aktiv via "Skip" weggeklickt hat. */
  dismissedCount = 0;
  progressPct = 0;
  openCount = 0;

  /**
   * One-Shot-Pulse, getriggert wenn der Coordinator eine Tour als pausiert
   * meldet. CSS-Animation laeuft 700 ms, danach setzen wir die Klasse zurueck.
   */
  pulseActive = false;
  /**
   * Schutz gegen wiederholtes Auto-Open: das SessionStorage-Flag wird beim
   * ersten Tick verbraucht, dieses In-Memory-Flag verhindert, dass eine
   * spätere Tick-Folge (oder ein Re-Mount in derselben Session) das Panel
   * erneut aufzwingt. Gesetzt von `maybeAutoOpenOnce()`.
   */
  private autoOpenConsumed = false;
  /**
   * Resume-Hinweis "Pausiert — hier weitermachen". 3.6 s sichtbar (CSS-
   * Out-Anim startet bei 3.28 s, dauert 0.32 s); danach Flag zurueck. Wird
   * sofort verworfen, wenn der User in der Zwischenzeit das Panel oeffnet.
   */
  toastVisible = false;

  /**
   * Sichtbarkeit der "Übersprungen"-Liste. Default eingeklappt: User hat
   * die Items aktiv weggeklickt, sie sollen nicht mehr Aufmerksamkeit
   * konkurrenzieren. Header bleibt aber sichtbar fuer Auffindbarkeit.
   */
  dismissedExpanded = false;

  /**
   * Sichtbarkeit des ⋮-Overflow-Menues im Panel-Header (Assistent
   * verwalten/ausblenden). Schliesst per Outside-Click (HostListener) und
   * Escape, sowie nach Auswahl eines Eintrags.
   */
  manageMenuOpen = false;

  // ───── Done-State ──────────────────────────────────────────────────
  /**
   * Abgeleitetes Flag: alle Goldpfad-Touren sind mindestens einmal echt
   * abgeschlossen (Completed) ODER ihr Ziel wurde auto-erkannt
   * (AutoCompleted) ODER laufen gerade als Replay (Started + completedAt).
   * Damit ist der Done-State stabil gegen einzelne Replays — solange
   * `completedAt` im Backend persistent ist.
   */
  allDone = false;
  /** True genau fuer 600 ms beim erstmaligen Erreichen von 6/6 mit
   *  offenem Panel. Triggert Sweep, Header-Crossfade, Haekchen-Kaskade. */
  celebrating = false;
  /**
   * Brueckt den Fall, dass der 6/6-Moment eintritt, waehrend das Panel zu
   * ist (typisch: User schliesst die letzte Tour, das Panel ist beim
   * Tour-Start ohnehin geschlossen worden). Wir merken uns den ausstehenden
   * Trigger und feuern ihn beim naechsten Oeffnen — solange der LocalStorage-
   * Flag noch nicht gesetzt ist. Wird bei !allDone wieder zurueckgesetzt.
   */
  private celebrationPending = false;
  /** Persistenter "Setup ausgeblendet"-Status. Aus LocalStorage gehydratet,
   *  spaeter optional aus Backend-Feld OnboardingState.SetupDismissedAt. */
  setupDismissedAt: Date | null = null;
  /** View-Modus, getrieben durch `recomputeViewMode()`. Steuert Template-
   *  Switch und CSS-Klassen. */
  viewMode: 'expanded' | 'collapsed-progress' | 'celebration'
          | 'collapsed-done' | 'dismissed' = 'collapsed-progress';

  private celebrationTimer: number | null = null;
  /** LocalStorage-Key: ISO-Datum, wann die Celebration-Sequenz erstmals
   *  lief. Verhindert, dass die Animation bei einem spaeteren Replay oder
   *  Reload erneut startet. */
  private readonly LS_KEY_CELEBRATION_SEEN = 'xp.onboarding.celebrationSeenAt';
  /** LocalStorage-Key: ISO-Datum, wann der User "Setup ausblenden" geklickt
   *  hat ODER der 30-Tage-Auto-Dismiss zugegriffen hat. */
  private readonly LS_KEY_DISMISSED_AT = 'xp.onboarding.setupDismissedAt';
  /** Auto-Dismiss-Schwelle: 30 Tage nach dem ersten Done verschwindet
   *  die Bubble von selbst, falls der User sie nie aktiv ausgeblendet hat. */
  private readonly AUTO_DISMISS_DAYS = 30;

  private sub?: Subscription;
  private pulseTimer: number | null = null;
  private toastTimer: number | null = null;

  /**
   * Goldpfad-Reihenfolge des fachlichen Setup-Funnels. Reihenfolge ist die
   * verbindliche Quelle der Wahrheit fuer UI, Trigger-Prioritaeten und
   * AfterSuccess-Verkettung — alle drei muessen dieselbe Geschichte erzaehlen:
   *   1. Unternehmensdaten   — Basis fuer alle Folge-Schritte
   *   2. Erste Abteilung     — strukturelle Voraussetzung fuer Mitarbeiter
   *   3. Qualifikationen     — danach, weil ohne Abteilung nichts zum
   *                            Zuweisen da ist; Skill-Multi-Select im Dept-
   *                            Edit-Dialog kann spaeter nachtragen
   *   4. Erster Mitarbeiter  — braucht Abteilung (+ optional Qualifikationen)
   *   5. Personalbedarfsplanung — Endziel: Schichten besetzen
   *
   * Synchron zu Priority-Werten in den Tour-Configs:
   * CompanyInfo=10, FirstDepartment=20, AddSkills=30, FirstEmployee=40,
   * StaffingManagement=55.
   */
  readonly mainOrder: {
    id: TourFeature;
    titleKey: string;
    descKey: string;
  }[] = [
    {
      id: 'company-info' as TourFeature,
      titleKey: 'ONBOARDING_CHECKLIST.TOUR.COMPANY_INFO.TITLE',
      descKey: 'ONBOARDING_CHECKLIST.TOUR.COMPANY_INFO.DESC',
    },
    {
      id: 'first-department' as TourFeature,
      titleKey: 'ONBOARDING_CHECKLIST.TOUR.FIRST_DEPARTMENT.TITLE',
      descKey: 'ONBOARDING_CHECKLIST.TOUR.FIRST_DEPARTMENT.DESC',
    },
    {
      id: 'add-skills' as TourFeature,
      titleKey: 'ONBOARDING_CHECKLIST.TOUR.ADD_SKILLS.TITLE',
      descKey: 'ONBOARDING_CHECKLIST.TOUR.ADD_SKILLS.DESC',
    },
    {
      id: 'first-employee' as TourFeature,
      titleKey: 'ONBOARDING_CHECKLIST.TOUR.FIRST_EMPLOYEE.TITLE',
      descKey: 'ONBOARDING_CHECKLIST.TOUR.FIRST_EMPLOYEE.DESC',
    },
    // AP-0/AP-1 (Planning-Onboarding "Mentor"): Die Legacy-Planungstouren
    // 'shift-planning' (erste Schicht) und 'staffing-management'
    // (Personalbedarf→Schichtzuweisung) wurden aus der Setup-Checkliste
    // entfernt (lehrten das abgeloeste Zwei-Schritte-Modell). Der Mentor belegt
    // jetzt den Planungs-Slot: erst das Pre-Flight (Voraussetzungen), dann die
    // gefuehrte erste Planung.
    {
      id: 'mentor-entry' as TourFeature,
      titleKey: 'ONBOARDING_CHECKLIST.TOUR.MENTOR_ENTRY.TITLE',
      descKey: 'ONBOARDING_CHECKLIST.TOUR.MENTOR_ENTRY.DESC',
    },
    // Mitarbeiterzeiterfassung — manuell Zeiten korrigieren / nachbuchen.
    // Schliesst den Goldpfad nach der Bedarfsplanung ab: erst plant der
    // Manager Schichten, dann erfasst er reale Zeiten gegen die Planung.
    {
      id: 'time-recording' as TourFeature,
      titleKey: 'ONBOARDING_CHECKLIST.TOUR.TIME_RECORDING.TITLE',
      descKey: 'ONBOARDING_CHECKLIST.TOUR.TIME_RECORDING.DESC',
    },
    // Aufgaben (B-10) — siebter Schritt, priority 70. Ziel: erste Aufgabe
    // angelegt und zugewiesen.
    //
    // ⚠️ NUR rechte-, nicht feature-gegated: `isAllowed()` unten kennt allein
    // `cfg.requiredPermissions`, und `TourConfig` hat kein Feld fuer ein
    // Abo-Feature. Das Aufgaben-Gate ist aber zweiachsig (feature.tasks =
    // Seed-Id 64 UND ≥1 View-Recht, siehe `modules/tasks/gate/tasks-gate.helper.ts`).
    // Praktisch faellt das heute nicht auf, weil `UserPermissionConfig` noch gar
    // keinen `Tasks`-Block hat und die Rechte-Achse deshalb fuer alle schliesst.
    // Sobald der Block ausgeliefert wird, braucht dieser Eintrag zusaetzlich
    // eine Feature-Pruefung — sonst sieht ein Kunde OHNE das Abo-Feature einen
    // Setup-Schritt, dessen Route der `TasksFeatureGuard` auf die Abo-Seite
    // umleitet.
    {
      id: 'tasks' as TourFeature,
      titleKey: 'ONBOARDING_CHECKLIST.TOUR.TASKS.TITLE',
      descKey: 'ONBOARDING_CHECKLIST.TOUR.TASKS.DESC',
    },
  ];

  constructor(
    private readonly coordinator: TourCoordinator,
    private readonly state: TourStateStore,
    private readonly registry: TourRegistry,
    private readonly auth: AuthService,
    private readonly featureFlags: FeatureFlagsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly datePipe: DatePipe,
    private readonly translate: TranslateService,
    private readonly progressService: OnboardingProgressService,
    private readonly userPermissions: UserPermissionsService,
    private readonly userSettings: UserSettingsService,
    private readonly dialog: MatDialog,
    private readonly toastr: ToastrService,
    private readonly host: ElementRef<HTMLElement>,
    private readonly helpHub: HelpHubStateService,
  ) {}

  /**
   * Done-Header — relatives Datum: "Heute abgeschlossen" / "Gestern
   * abgeschlossen" / "vor 3 Tagen abgeschlossen" / absolutes Datum ab
   * 7 Tagen. GitHub-/Linear-Pattern. In 99 % der Faelle sieht der User
   * den Done-State direkt am Done-Tag — "Heute" liest sich warmer und
   * konkreter als "30.04.2026". Der TranslateService liefert lokalisierte
   * Strings; Plurale werden via i18n-Interpolation aufgeloest.
   */
  get setupCompletedAtRelative(): string | null {
    const d = this.setupCompletedAt;
    if (!d) return null;
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const startOfDate = new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
    );
    const dayDiff = Math.round(
      (startOfToday.getTime() - startOfDate.getTime()) /
        (1000 * 60 * 60 * 24),
    );
    if (dayDiff <= 0) {
      return this.translate.instant('ONBOARDING_CHECKLIST.COMPLETED_TODAY');
    }
    if (dayDiff === 1) {
      return this.translate.instant('ONBOARDING_CHECKLIST.COMPLETED_YESTERDAY');
    }
    if (dayDiff < 7) {
      return this.translate.instant(
        'ONBOARDING_CHECKLIST.COMPLETED_DAYS_AGO',
        { days: dayDiff },
      );
    }
    const formatted = this.datePipe.transform(d, 'mediumDate') ?? '';
    return this.translate.instant('ONBOARDING_CHECKLIST.COMPLETED_ON', {
      date: formatted,
    });
  }

  /**
   * Absolutes Datum als xdsTooltip-Inhalt — fuer User, die das genaue
   * Datum sehen wollen, wenn der relative String reicht ("Heute"). Format
   * inkl. Uhrzeit, damit klar ist wann genau abgeschlossen wurde.
   */
  get setupCompletedAtAbsolute(): string | null {
    const d = this.setupCompletedAt;
    if (!d) return null;
    return this.datePipe.transform(d, 'medium') ?? null;
  }

  /**
   * Done-Footer-CTA. Schliesst das Panel und navigiert zur Schichtplanung —
   * dem natuerlichen "ich bin fertig mit Setup, jetzt arbeiten"-Einstieg.
   */
  planFirstShift(): void {
    this.open = false;
    this.recomputeViewMode(this.allDone);
    // D-12 (E1): Personalbedarf → ▦-Muster-Modus der Planung
    void this.router.navigate(['/planner'], { queryParams: { view: 'muster' } });
    this.cdr.markForCheck();
  }

  /**
   * Einstieg aus der Verwalten-Zeile: Sicherheitsabfrage vor dem
   * Ausblenden. Der Bestaetigungs-Dialog erklaert den Rueckweg
   * (Benutzereinstellungen → Hilfe & Einrichtung). Erst bei Bestaetigung
   * wird `ShowOnboardingAssistant=false` server-persistiert.
   */
  /**
   * ⋮-Overflow im Panel-Header auf/zu. `stopPropagation`, damit der gleiche
   * Klick nicht sofort vom Outside-Click-HostListener wieder geschlossen
   * wird.
   */
  toggleManageMenu(event: Event): void {
    event.stopPropagation();
    this.manageMenuOpen = !this.manageMenuOpen;
    this.cdr.markForCheck();
  }

  /**
   * Oeffnet das Erklaervideo zu einem Checklisten-Schritt.
   *
   * Das Hub-Panel wird dabei geschlossen: Es sitzt als Overlay unten rechts
   * und laege sonst ueber dem Dialog. Der Schritt-Status bleibt unangetastet —
   * ein Video zaehlt bewusst nicht als erledigt (siehe `help-video.config.ts`).
   */
  openVideo(video: HelpVideo | undefined): void {
    if (!video) return;
    this.helpHub.close();
    // Groesse ueber die Stufe `xl` statt roher Pixelwerte: Das ist laut
    // `shared/ui/dialog-shell/dialog-config.ts` die „Arbeitsflaechen-Bauform"
    // (92vw / 1400px mit eigener Hoehe) — genau das ist ein Videoplayer.
    // `maxHeight` entfaellt bewusst: `.xds-dialog-shell` deckelt zentral auf 92dvh,
    // ein zweiter Wert waere eine konkurrierende Quelle fuer dieselbe Zahl.
    this.dialog.open(HelpVideoDialogComponent, xdsDialogConfig('xl', {
      panelClass: 'custom-dialog-container',
      autoFocus: 'first-tabbable',
      restoreFocus: true,
      data: { video },
    }));
  }

  /**
   * Sekundaer-Eintrag: oeffnet die Benutzereinstellungen (echter
   * AdminUserSettingsDialog), wo der Assistent ueber den Toggle
   * "Hilfe & Einrichtung" wieder eingeblendet werden kann. Spiegelt den
   * Re-Aktivierungs-Pfad direkt neben dem Ausblenden.
   */
  openSettings(): void {
    this.manageMenuOpen = false;
    this.dialog.open(AdminUserSettingsDialogComponent, {
      width: '700px',
      maxWidth: '95vw',
      maxHeight: '85vh',
      panelClass: 'admin-user-settings-dialog',
    });
  }

  requestHideAssistant(): void {
    this.manageMenuOpen = false;
    const ref = this.dialog.open(ConfirmationDialogComponent, {
      width: '420px',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      autoFocus: 'first-tabbable',
      restoreFocus: true,
      data: {
        type: 'warning',
        iconClass: 'fa-light fa-eye-slash',
        title: this.translate.instant('HIDE_SETUP_CONFIRM_TITLE'),
        message: this.translate.instant('HIDE_SETUP_CONFIRM_BODY'),
        // `details` rendert als hervorgehobene Hinweis-Box → Rueckweg.
        details: this.translate.instant('HIDE_SETUP_CONFIRM_RESTORE_HINT'),
        confirmText: this.translate.instant('ONBOARDING_CHECKLIST.HIDE_SETUP'),
        cancelText: this.translate.instant('CANCEL'),
        confirmButtonClass: 'btn-primary',
      },
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed === true) {
        this.hideAssistant();
      }
    });
  }

  /**
   * Persistiert die Ausblendung server-seitig ueber den kanonischen
   * Schalter `UserConfig.Dashboard.ShowOnboardingAssistant`. Die reaktive
   * Kette (FeatureFlagsService → onboardingGloballyDisabled$) blendet
   * daraufhin Tab UND FAB ueberall geraeteuebergreifend aus.
   */
  private hideAssistant(): void {
    const current = this.userSettings.getCurrentUserSettingsSnapshot();
    if (!current) return;
    const updated = UserConfig.fromData({
      ...current,
      Dashboard: { ...current.Dashboard, ShowOnboardingAssistant: false },
    });
    this.userSettings.updateUserSettings(updated).subscribe((ok) => {
      if (ok) {
        this.toastr.success(this.translate.instant('HIDE_SETUP_TOAST'));
      }
    });
  }

  ngOnInit(): void {
    // Versteckter Support-Restore-Pfad: Aufruf der App mit
    // ?xpResetOnboarding=1 loescht beide Done-State-LS-Keys, bevor sie
    // gelesen werden. Dient als Backup-Mechanismus, solange es noch
    // keinen Help-Menue-Eintrag "Einrichtungs-Tour erneut starten" gibt
    // (Folge-PR). Verbraucht den URL-Param und entfernt ihn aus der URL,
    // damit Reloads den Reset nicht endlos triggern.
    try {
      if (typeof window !== 'undefined' && window.location) {
        const url = new URL(window.location.href);
        if (url.searchParams.get('xpResetOnboarding') === '1') {
          this.removeLocalStorage(this.LS_KEY_CELEBRATION_SEEN);
          this.removeLocalStorage(this.LS_KEY_DISMISSED_AT);
          url.searchParams.delete('xpResetOnboarding');
          window.history.replaceState({}, '', url.toString());
        }
      }
    } catch {
      /* in Capacitor / SSR / restriktivem Sandboxing leise ignorieren */
    }

    // Done-State aus LocalStorage hydratieren (manueller Dismiss). Das
    // muss VOR der ersten rebuild-Subscription passieren, sonst flackert
    // der erste Render durch die Pille auf, bevor sie wieder verschwindet.
    const dismissedRaw = this.readLocalStorage(this.LS_KEY_DISMISSED_AT);
    if (dismissedRaw) {
      const parsed = new Date(dismissedRaw);
      if (!isNaN(parsed.getTime())) {
        this.setupDismissedAt = parsed;
      }
    }

    // 30-Tage-Auto-Dismiss: wenn der Celebration-Moment so lange her ist
    // und der User nie aktiv "Ausblenden" geklickt hat, blenden wir die
    // Bubble jetzt aus. Das verhindert, dass die Bubble bei Power-Usern
    // dauerhaft im Weg steht — sie ist im Help-Menue weiterhin erreichbar.
    if (!this.setupDismissedAt) {
      const celebRaw = this.readLocalStorage(this.LS_KEY_CELEBRATION_SEEN);
      if (celebRaw) {
        const seen = new Date(celebRaw);
        const ageMs = Date.now() - seen.getTime();
        const ageDays = ageMs / (1000 * 60 * 60 * 24);
        if (!isNaN(seen.getTime()) && ageDays >= this.AUTO_DISMISS_DAYS) {
          const now = new Date();
          this.setupDismissedAt = now;
          this.writeLocalStorage(
            this.LS_KEY_DISMISSED_AT,
            now.toISOString(),
          );
        }
      }
    }

    this.sub = combineLatest([
      this.featureFlags.onboardingGloballyDisabled$,
      this.progressService.progress$,
      this.userPermissions.currentUserPermissionsObservable,
      ...this.mainOrder.map((m) => this.state.getState(m.id)),
    ]).subscribe(([disabled, progress, perms, ...snapshots]) => {
      // Zentraler Kill-Switch aus FeatureFlagsService — konsistent mit
      // TourCoordinator, WelcomeCard, DashboardIntroService und Nudge.
      // Dev-Mode-Override fuer Design-Reviews und Tests.
      const showAssistant = !disabled || isDevMode();
      const audienceOk =
        this.auth.isAdmin || this.auth.isManegeOrTeamLeader;
      // Wenn dem User fuer keinen einzigen Setup-Schritt die Permission
      // fehlt → keine Liste, kein FAB. Verhindert leere Bubble bei
      // ReadOnly-Rollen oder eng zugeschnittenen Templates.
      const hasAnyAllowedTour = (progress as OnboardingProgress).total > 0;
      this.visible = showAssistant && audienceOk && hasAnyAllowedTour;
      // Aggregat-Werte (Counter, allDone, progressPct) kommen aus dem
      // OnboardingProgressService — dieselbe Quelle wie die WelcomeCard
      // nutzt. Damit zeigen beide Widgets garantiert dieselbe "X von N"-Zahl.
      this.rebuild(
        snapshots as TourStateSnapshot[],
        progress as OnboardingProgress,
        perms as UserPermissionConfig,
      );
      // Nach RegisteredStepper → Settings-Redirect (Pflichtfelder fehlen):
      // wenn das Stepper-Flag gesetzt ist, Panel einmalig aufklappen.
      // Wir prüfen NACH `rebuild`, damit `allDone` aktuell ist.
      this.maybeAutoOpenOnce();
      this.cdr.markForCheck();
    });

    // Pause-Signal vom Coordinator — wenn der User die Tour per X-Klick
    // pausiert, geben wir kurz einen Resume-Hinweis: FAB pulsiert einmal,
    // ein dezenter Toast erscheint daruber. Wenn das Panel beim Pausieren
    // bereits offen ist, sieht der User den Resume-CTA ohnehin direkt —
    // der Toast ist dann redundant und wird unterdrueckt.
    this.sub.add(
      this.coordinator.tourPaused$.subscribe(() => this.onTourPaused()),
    );

    // Zentrale Garantie: sobald irgendeine Tour aktiv wird, faltet sich das
    // Panel automatisch zu. Das deckt alle Start-Pfade ab — Activate aus der
    // Liste, Restore (Wieder anzeigen), Sim-FAB, Welcome-Card,
    // programmatische Starts. Ohne diese Sub muesste jede activate-Methode
    // separat `this.open = false` setzen, und neue Code-Pfade wuerden den
    // Reflex vergessen — Panel und Tour-Popover wuerden sich dann am
    // Bildschirm ueberlappen.
    // `skip(1)` ueberspringt den synchronen Replay-Wert des BehaviorSubjects
    // beim Subscribe. Wir wollen nur auf einen ECHTEN Wechsel (eine Tour wird
    // neu aktiv) reagieren, nicht auf den aktuellen Stand beim Mount. Das ist
    // im embedded-Modus entscheidend: die Checkliste wird bei jedem Oeffnen des
    // Hubs neu erzeugt; ohne skip wuerde der replayte (noch laufende) Tour-Wert
    // den gerade geoeffneten Hub sofort wieder zuklappen — der User koennte das
    // Panel waehrend einer Tour gar nicht mehr aufklappen.
    this.sub.add(
      this.coordinator.activeTour$
        .pipe(skip(1))
        .subscribe((activeTour) => {
          if (activeTour === null) return;
          // Im embedded-Modus rendert die Checkliste im Help-Hub-Panel. Das
          // eigene `open`-Flag steuert es dann nicht — wir muessen den Hub
          // selbst zuklappen, sonst ueberlagert das aufgeklappte Panel rechts
          // das gerade gestartete Tour-Popover und der User muss es haendisch
          // per X schliessen. Im Standalone-Modus genuegt wie bisher `open=false`.
          if (this.embedded) {
            this.helpHub.close();
          } else if (this.open) {
            this.open = false;
            this.cdr.markForCheck();
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.clearPulseTimer();
    this.clearToastTimer();
    this.clearCelebrationTimer();
  }

  toggle(): void {
    // Wenn der User selbst auf den FAB klickt waehrend der Resume-Toast
    // sichtbar ist, dismissed der Toast sofort — die Botschaft ist
    // angekommen, alles weitere zu zeigen waere Larm.
    if (this.toastVisible) {
      this.dismissToast();
    }
    this.open = !this.open;
    // View-Modus neu bestimmen — beim Oeffnen einer 6/6-Bubble wechseln
    // wir z. B. von 'collapsed-done' auf 'celebration' (Done-Panel).
    // Wir uebergeben `this.allDone` als wasAllDoneBefore: das verhindert,
    // dass das Oeffnen alleine als "justFlipped" missgedeutet wird und
    // die Animation startet.
    this.recomputeViewMode(this.allDone);
  }

  /**
   * Einmalige Auto-Open-Brücke vom RegisteredStepper. Wird beim ersten
   * Subscribe-Tick nach Mount gelesen: liegt im SessionStorage das Flag
   * `onboarding.checklist.autoOpenOnce`, klappt das Panel automatisch
   * auf — exakt einmal pro Session. Das Flag wird sofort konsumiert,
   * damit weder eine spätere Re-Emission der `combineLatest`-Quelle
   * noch ein Reload des Settings-Tabs das Panel erneut aufzwingt.
   *
   * Bedingungen: nur wenn FAB sichtbar (Kill-Switch + Audience OK),
   * nicht im allDone-Pill-State, und Panel nicht bereits offen.
   * Verbraucht das Flag aber in jedem Fall — `autoOpenConsumed` macht
   * den In-Memory-Lock, falls SessionStorage clearen scheitert.
   */
  private maybeAutoOpenOnce(): void {
    if (this.autoOpenConsumed) return;
    let flag: string | null = null;
    try {
      flag = sessionStorage.getItem('onboarding.checklist.autoOpenOnce');
    } catch {
      return;
    }
    if (flag !== 'true') return;
    this.autoOpenConsumed = true;
    try {
      sessionStorage.removeItem('onboarding.checklist.autoOpenOnce');
    } catch {
      /* sessionStorage kann in restriktiven Embeds fehlschlagen — ignorieren */
    }
    if (!this.visible || this.allDone || this.open) return;
    this.open = true;
    this.recomputeViewMode(this.allDone);
  }

  /**
   * Reaktion auf `coordinator.tourPaused$`: One-Shot-Pulse + Resume-Toast.
   * Beide laufen unabhaengig, aber gleichzeitig: der Pulse zieht das Auge
   * zum FAB, der Toast erklaert in Worten, was passiert ist.
   */
  private onTourPaused(): void {
    if (this.open) return;
    this.clearPulseTimer();
    this.clearToastTimer();
    this.pulseActive = true;
    this.toastVisible = true;
    this.cdr.markForCheck();
    this.pulseTimer = window.setTimeout(() => {
      this.pulseActive = false;
      this.pulseTimer = null;
      this.cdr.markForCheck();
    }, 700);
    // Toast-CSS startet die Out-Animation bei 3.28 s und ist nach
    // 3.6 s vollstaendig ausgeblendet — dann erst entfernen wir den Knoten,
    // damit die Animation nicht mid-flight gestoppt wird.
    this.toastTimer = window.setTimeout(() => {
      this.toastVisible = false;
      this.toastTimer = null;
      this.cdr.markForCheck();
    }, 3600);
  }

  private dismissToast(): void {
    this.clearToastTimer();
    this.toastVisible = false;
  }

  private clearPulseTimer(): void {
    if (this.pulseTimer !== null) {
      clearTimeout(this.pulseTimer);
      this.pulseTimer = null;
    }
  }

  private clearToastTimer(): void {
    if (this.toastTimer !== null) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    // Escape schliesst zuerst das ⋮-Menue (falls offen), erst danach das
    // Panel — sonst klappt ein einziger Escape-Druck beides gleichzeitig zu.
    if (this.manageMenuOpen) {
      this.manageMenuOpen = false;
      this.cdr.markForCheck();
      return;
    }
    if (this.open) {
      this.open = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Outside-Click schliesst das ⋮-Overflow-Menue. Der Kebab-Toggle selbst
   * ruft `stopPropagation`, sodass der oeffnende Klick hier nicht ankommt.
   * Klicks innerhalb des Host (= im Menue) werden ignoriert.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.manageMenuOpen) return;
    const target = event.target as Node | null;
    if (target && this.host.nativeElement.contains(target)) return;
    this.manageMenuOpen = false;
    this.cdr.markForCheck();
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }

  /**
   * Voraussetzungs-Graph importiert aus der gemeinsamen Quelle, damit
   * FAB und TourCoordinator (Nudge-Filter) konsistent dieselbe Logik
   * teilen. Vorher gab es eine private Kopie hier — das fuehrte zu
   * Inkonsistenz: FAB sagte "GESPERRT", aber der Top-Nudge bot dieselbe
   * Tour als "Jetzt starten" an (Bug 2026-05-01).
   */
  private static readonly PREREQUISITES = TOUR_PREREQUISITES;

  /**
   * Liefert `true` fuer das Item, das als naechster Schritt angezeigt werden
   * soll. Priorisierung:
   *   - Ist bereits ein Item `started` (angefangen)? Dann bekommt dieses
   *     das Primary-Treatment via `is-active`, und `is-next` triggert nicht
   *     (vermeidet zwei gleichzeitige Primary-Buttons in der Liste).
   *   - Locked-Items werden grundsaetzlich uebersprungen — sie kommen erst
   *     "an die Reihe", wenn ihre Voraussetzungen erfuellt sind.
   *   - Sonst: das erste `pending`-Item in Reihenfolge. `pending` heisst hier
   *     `NeverSeen`, `Seen` ODER `Snoozed`. `Seen` darf nicht uebersprungen
   *     werden, weil der Status nur "FAB hat das Item gerendert" bedeutet,
   *     NICHT "User hat die Tour erlebt" — die Tour selbst ist noch nicht
   *     durchgelaufen.
   *     `Snoozed` zaehlt ebenfalls als pending (Fix 2026-06-15): ein "Spaeter"
   *     auf dem Nudge verschiebt nur das automatische Angebot fuer 24 h, es
   *     darf den Goldpfad-Zeiger aber NICHT auf eine spaetere Tour
   *     weiterspringen lassen. Sonst zeigt die Checklist "ALS NAECHSTES:
   *     Mitarbeiterzeiterfassung", obwohl die noch offene "Erste Planung"
   *     davor steht. Das 24-h-Snooze bleibt allein im Nudge-Funnel scharf
   *     (TourCoordinator.offerNextSetupTour), nicht in diesem Indikator.
   */
  private static isPendingStatus(s: TourStateStatus): boolean {
    return (
      s === TourStateStatus.NeverSeen ||
      s === TourStateStatus.Seen ||
      s === TourStateStatus.Snoozed
    );
  }

  isNext(t: { status: TourStateStatus; isLocked?: boolean }, index: number): boolean {
    if (t.isLocked) return false;
    if (!OnboardingChecklistFabComponent.isPendingStatus(t.status)) return false;
    const hasStarted = this.tours.some(
      (x) => x.status === TourStateStatus.Started && !x.isLocked,
    );
    if (hasStarted) return false;
    const firstPendingIdx = this.tours.findIndex(
      (x) =>
        !x.isLocked &&
        OnboardingChecklistFabComponent.isPendingStatus(x.status),
    );
    return firstPendingIdx === index;
  }

  /**
   * Wie {@link isNext}, aber arbeitet auf {@link primaryTours} (Hauptliste).
   * Verhindert dass "Als naechstes" auf einem Item gesetzt wird, das gar
   * nicht in der Hauptliste sichtbar ist (z. B. weil davor ein
   * AutoCompleted-Item das `firstPendingIdx` der vollen Liste war) ODER
   * dessen Voraussetzungen noch nicht erfuellt sind.
   */
  isPrimaryNext(
    t: { status: TourStateStatus; isLocked?: boolean },
    index: number,
  ): boolean {
    if (t.isLocked) return false;
    if (!OnboardingChecklistFabComponent.isPendingStatus(t.status)) return false;
    const hasStarted = this.primaryTours.some(
      (x) => x.status === TourStateStatus.Started && !x.isLocked,
    );
    if (hasStarted) return false;
    const firstPendingIdx = this.primaryTours.findIndex(
      (x) =>
        !x.isLocked &&
        OnboardingChecklistFabComponent.isPendingStatus(x.status),
    );
    return firstPendingIdx === index;
  }

  activate(t: {
    id: TourFeature;
    status: TourStateStatus;
    rawStatus?: TourStateStatus;
    isLocked?: boolean;
  }): void {
    // Locked-Items werden nicht direkt aktiviert — der User wird stattdessen
    // freundlich an den ersten fehlenden Vorgaenger weitergeleitet. So
    // kommt keine Tour mitten im Goldpfad ohne Voraussetzungen "an die
    // Reihe", aber der User landet auch nicht in einer Sackgasse.
    //
    // Ausnahme: bereits abgeschlossene Tours (Completed/AutoCompleted) werden
    // nie umgeleitet. Sie stehen in der "Bereits eingerichtet"-Sektion bzw.
    // tragen "Nochmal" — der User klickt explizit, weil er DIESE Tour
    // (wieder) sehen will. Ein Lock kann hier trotzdem aufflammen, wenn ein
    // Vorgaenger zwischenzeitlich snoozed wurde, aber inhaltlich sind die
    // Voraussetzungen erfuellt (sonst waere die Tour nicht abgeschlossen).
    // Ohne diese Ausnahme startete "Tour ansehen" auf einem auto-completed
    // Item den snoozed-Vorgaenger statt der eigentlich gewaehlten Tour
    // (Bug 2026-05-07: AutoCompleted "Erster Mitarbeiter" → add-skills-Tour).
    const alreadyAchieved =
      t.status === TourStateStatus.Completed ||
      t.status === TourStateStatus.AutoCompleted;
    if (t.isLocked && !alreadyAchieved) {
      const firstMissing = this.firstMissingPrerequisite(t.id);
      if (firstMissing) {
        this.activate(firstMissing);
      }
      return;
    }
    // Command-Wahl ueber den ECHTEN Backend-Status (rawStatus), NICHT den
    // evtl. remappten Anzeige-Status — sonst INVALID_STATE_TRANSITION (s. u.).
    const raw = t.rawStatus ?? t.status;
    const displayedAsDone = t.status === TourStateStatus.Completed;
    if (raw === TourStateStatus.Completed) {
      this.coordinator.replayTour(t.id, 'checklist');
    } else if (raw === TourStateStatus.AutoCompleted) {
      // User hat die Tour nie selbst gemacht, will sie jetzt anschauen.
      // fromStart: true → frisch von Schritt 1, kein Resume aus altem
      // lastStepId (gibt's bei auto-completed sowieso nicht).
      this.coordinator.startTour(t.id, { force: true, fromStart: true, source: 'checklist' });
    } else if (displayedAsDone) {
      // Real `started`/`snoozed`, aber als ERLEDIGT dargestellt (Replay ist am
      // Handoff-Hub am Ende „started" haengen geblieben). „Ansehen" = von vorn
      // ansehen. `replayTour` wuerde der Backend-Guard aus `started` ablehnen
      // (nur completed/dismissed erlaubt) → stattdessen `StartTour` mit
      // fromStart (Backend erlaubt das aus `started`, der Handler resettet den
      // Step-Counter). Behebt INVALID_STATE_TRANSITION (Bug 2026-06-17).
      this.coordinator.startTour(t.id, { force: true, fromStart: true, source: 'checklist' });
    } else {
      this.coordinator.startTour(t.id, { force: true, source: 'checklist' });
    }
    this.open = false;
    this.cdr.markForCheck();
  }

  /**
   * Findet den ersten Vorgaenger einer gelockten Tour, der noch nicht
   * abgeschlossen ist — fuer den "Klick auf locked → springe zum
   * Vorgaenger"-Pfad. Gibt das volle Tour-Item zurueck (mit Status etc.),
   * damit `activate()` rekursiv arbeiten kann.
   */
  private firstMissingPrerequisite(
    tourId: TourFeature,
  ):
    | { id: TourFeature; status: TourStateStatus; isLocked?: boolean }
    | undefined {
    const prereqs =
      OnboardingChecklistFabComponent.PREREQUISITES[tourId] ?? [];
    for (const p of prereqs) {
      const item = this.tours.find((x) => x.id === p);
      if (!item) continue;
      if (
        item.status !== TourStateStatus.Completed &&
        item.status !== TourStateStatus.AutoCompleted
      ) {
        return item;
      }
    }
    return undefined;
  }

  /**
   * Liefert den i18n-aufbereiteten Tooltip-Text fuer ein gelocktes Item:
   * "Erst <Vorgaenger 1> und <Vorgaenger 2> abschliessen".
   * Reagiert auf Sprachwechsel ueber den injected TranslateService.
   */
  lockTooltip(t: { lockReasonTitleKeys?: string[] }): string {
    const titles = (t.lockReasonTitleKeys ?? [])
      .map((k) => this.translate.instant(k))
      .filter((s) => !!s);
    if (titles.length === 0) return '';
    const joined =
      titles.length === 1
        ? titles[0]
        : titles.slice(0, -1).join(', ') + ' & ' + titles[titles.length - 1];
    return this.translate.instant('ONBOARDING_CHECKLIST.LOCKED_TOOLTIP', {
      titles: joined,
    });
  }

  /**
   * Restzeit-Schaetzung fuer den "~N Min."-Hinweis (docs/design-proposals/
   * setup-checklist-psychology-2026-07-21, Variante A). Bei bereits laufendem
   * Fortschritt wird die Tour-Gesamtzeit proportional zum verbleibenden
   * Schritt-Anteil heruntergerechnet, statt bei jedem Aufruf die volle
   * Schaetzung zu zeigen — sonst wirkt „noch 5 Min." bei Schritt 2 von 3
   * unehrlich. Rundet nie auf 0 ab (mindestens 1 Min.), solange ueberhaupt
   * eine Schaetzung vorliegt.
   */
  stepMinutes(t: {
    estimatedDurationSec?: number;
    stepProgress?: { done: number; total: number };
  }): number {
    const totalSec = t.estimatedDurationSec ?? 0;
    if (totalSec <= 0) return 0;
    const fraction =
      t.stepProgress && t.stepProgress.total > 0
        ? Math.max(0, 1 - t.stepProgress.done / t.stepProgress.total)
        : 1;
    return Math.max(1, Math.round((totalSec * fraction) / 60));
  }

  /**
   * "Skip" auf einer Auto-Completed-Tour: User entscheidet aktiv, dass er
   * die Tour nicht braucht (Daten sind ja schon da). Wir setzen Status
   * auf Dismissed → Item bleibt in der Liste sichtbar (mit eigener Visualisierung)
   * und kann via "Wieder anzeigen" (= Replay) wieder geholt werden.
   */
  skip(t: { id: TourFeature; status: TourStateStatus }): void {
    if (t.status !== TourStateStatus.AutoCompleted) return;
    void this.state.transitionDismissed(t.id);
    this.cdr.markForCheck();
  }

  /**
   * "Wieder anzeigen" auf einer dismissed Tour: User moechte die Tour
   * doch noch ansehen koennen. Backend-Replay-Endpoint setzt Status auf
   * Started zurueck → Item wird wieder zu einer regulaeren laufenden Tour.
   */
  restore(t: { id: TourFeature; status: TourStateStatus }): void {
    if (t.status !== TourStateStatus.Dismissed) return;
    this.coordinator.replayTour(t.id);
    this.cdr.markForCheck();
  }

  /** Klappt die "Übersprungen"-Liste auf/zu. */
  toggleDismissed(): void {
    this.dismissedExpanded = !this.dismissedExpanded;
    this.cdr.markForCheck();
  }

  private rebuild(
    snapshots: TourStateSnapshot[],
    progress: OnboardingProgress,
    perms: UserPermissionConfig | null | undefined,
  ): void {
    // Permission-Filter: ein Schritt, fuer den dem User die noetige
    // Berechtigung fehlt, gehoert weder in die Hauptliste noch in die
    // "Bereits eingerichtet"/"Uebersprungen"-Sektionen. Sobald die
    // Berechtigung nachgeliefert wird (Admin aktualisiert die Rolle), kippt
    // das Observable und der Schritt erscheint reaktiv wieder.
    const isAllowed = (id: TourFeature): boolean => {
      const cfg = this.registry.get(id);
      return hasAllPermissions(perms, cfg?.requiredPermissions);
    };
    // Status-Lookup pro TourFeature, damit isUnlocked die Voraussetzungen
    // nachschlagen kann.
    const statusById = new Map<TourFeature, TourStateStatus>();
    // Achievement-Lookup pro TourFeature: zaehlt completed UND auto-completed
    // UND alle Faelle, in denen das Tour-Ziel mal erreicht wurde, ohne dass
    // der Status aktuell terminal ist (Started nach Tour-Preview behaelt
    // autoCompletedAt). Wird von der Lock-Berechnung der Folge-Tours genutzt,
    // damit nicht eine voruebergehende "Tour ansehen"-Phase bei einem Prereq
    // den ganzen Folge-Funnel auf GESPERRT setzt (Bug 2026-05-07).
    const achievedById = new Map<TourFeature, boolean>();
    this.mainOrder.forEach((m, i) => {
      statusById.set(m.id, snapshots[i]?.status ?? TourStateStatus.NeverSeen);
      const snap = snapshots[i];
      // Permission-gefilterte Schritte zaehlen fuer die Lock-Berechnung der
      // Folge-Tours als "erfuellt": sonst saehe ein TeamLeader ohne
      // Settings-Permission alle nachfolgenden Schritte als GESPERRT
      // ("Erst Unternehmensdaten abschliessen ..."), obwohl der versteckte
      // Vorgaenger ihn gar nicht betrifft.
      const allowed = isAllowed(m.id);
      achievedById.set(
        m.id,
        !allowed || !!snap?.completedAt || !!snap?.autoCompletedAt,
      );
    });
    const titleKeyById = new Map<TourFeature, string>(
      this.mainOrder.map((m) => [m.id, m.titleKey]),
    );

    const tours = this.mainOrder.filter((m) => isAllowed(m.id)).map((m) => {
      const i = this.mainOrder.findIndex((x) => x.id === m.id);
      const snap = snapshots[i];
      const status = snap?.status ?? TourStateStatus.NeverSeen;
      const wasEverAchievedItem =
        !!snap?.completedAt || !!snap?.autoCompletedAt;
      // Einmal pro Item aus der Registry gezogen — liefert u. a.
      // estimatedDurationSec (Zeitschaetzung, s. u.) UND wird vom bestehenden
      // Started-Zweig fuer die Schritt-Gesamtzahl weiterverwendet.
      const cfg = this.registry.get(m.id);
      let stepProgress: { done: number; total: number } | undefined;
      // Anzeige-Status: kann vom rohen State-Status abweichen (s. u. der
      // „Replay am Tour-Ende"-Fall), damit das Item NICHT faelschlich
      // „Fortsetzen" anbietet, wenn es nichts Sinnvolles fortzusetzen gibt.
      let displayStatus = status;
      if (status === TourStateStatus.Started) {
        const total =
          cfg?.chapters.reduce((s, c) => s + c.steps.length, 0) ??
          snap?.totalStepCount ??
          0;
        const done = snap?.completedStepCount ?? 0;
        // Degenerierter „Fortsetzen"-Fall (User-Report 2026-06-17): Eine
        // schon einmal abgeschlossene Tour, die per Replay bis ans ENDE lief
        // (done >= total) und dort 'started' HAENGEN bleibt — typisch, weil
        // ihr letzter Schritt ein Handoff-Hub ist, der an eine Folge-Tour
        // uebergibt (kein transitionStepCompleted). Die Checkliste zeigte
        // dann „Fortsetzen 7/7"; ein Klick startete die Tour aber von vorn und
        // landete auf dem Hauptknoten („1/1"-Weiche). Solche Items als
        // ERLEDIGT darstellen (Haken + „Nochmal"), nicht als fortsetzbar.
        const reachedEnd = total > 0 && done >= total;
        if (reachedEnd && wasEverAchievedItem) {
          displayStatus = TourStateStatus.Completed;
        } else {
          // Step-Counter aus DERSELBEN Resume-Quelle wie der Planner-Chip
          // ({@link TourCoordinator.getResumeProgress}) — so stimmt die
          // angezeigte Zahl mit der Stelle ueberein, an der der „Fortsetzen"-
          // Klick wirklich landet (renumberVisibleSteps-Touren zeigten sonst
          // einen Rohzaehler wie „7/7", obwohl der Resume sichtbar bei „2/4"
          // ankommt). Fallback auf den Rohzaehler, wenn kein gueltiger
          // Resume-Punkt vorliegt (z. B. frischer Replay ohne lastStepId).
          const rp = this.coordinator.getResumeProgress(m.id);
          // Step-Counter nur zeigen, wenn er semantisch zu Status + Achievement
          // passt: bei Replay-Runs (achieved + 0 Fortschritt) widerspricht
          // „0/7" dem gruenen Haken. Erst ab Step 1 schalten wir ihn ein.
          const showCounter = !wasEverAchievedItem || done > 0;
          if (showCounter) {
            stepProgress = rp
              ? { done: rp.stepNumber, total: rp.totalSteps }
              : total > 0
                ? { done, total }
                : undefined;
          }
        }
      }

      // Lock-Berechnung: alle deklarierten Vorgaenger muessen 'completed'
      // ODER 'auto-completed' sein, sonst ist diese Tour gesperrt. 'Started'
      // mit Voraussetzungs-Check — eine bereits angefangene Tour, deren
      // Voraussetzung wieder fehlt (z. B. weil der User Daten geloescht hat),
      // wird trotzdem gelocked: User soll erst den Vorgaenger bereinigen.
      // Ausnahme: wenn das Tour-Ziel selbst schon erreicht wurde
      // (completedAt oder autoCompletedAt gesetzt), greift kein Lock — die
      // narrative Reihenfolge der Prereqs ist hier irrelevant, das Datenziel
      // ist bereits da. Sonst wuerde ein "Tour ansehen"-Klick auf eine
      // AutoCompleted-Tour sie als GESPERRT zeigen, sobald ein Prereq
      // wegen Snooze nicht completed ist (Bug 2026-05-07).
      const wasEverAchieved =
        !!snap?.completedAt || !!snap?.autoCompletedAt;
      const prereqs =
        OnboardingChecklistFabComponent.PREREQUISITES[m.id] ?? [];
      // Ein Prereq gilt als erfuellt, wenn er aktuell terminal-completed
      // ist ODER mal achievement gesetzt wurde (Tour wird gerade als
      // Replay angesehen). Sonst kaskadiert ein "Tour ansehen" auf einem
      // Prereq alle Folge-Tours auf GESPERRT.
      const missing = prereqs.filter((p) => {
        const s = statusById.get(p);
        if (
          s === TourStateStatus.Completed ||
          s === TourStateStatus.AutoCompleted
        ) {
          return false;
        }
        return !achievedById.get(p);
      });
      const isLocked = !wasEverAchieved && missing.length > 0;
      // Fuer den Tooltip die Titel der fehlenden Vorgaenger zusammenfassen
      // (i18n-Resolution macht das Template).
      const lockReasonTitleKeys = missing
        .map((p) => titleKeyById.get(p))
        .filter((k): k is string => !!k);

      return {
        id: m.id,
        titleKey: m.titleKey,
        descKey: m.descKey,
        // `undefined`, solange keine YouTube-ID eingetragen ist — der Knopf
        // erscheint dann gar nicht erst.
        video: helpVideoFor(m.id),
        // Anzeige-Status (kann „started am Ende" → „completed" remappen, s. o.).
        status: displayStatus,
        // ECHTER Backend-Status (vor dem Remap) — `activate()` waehlt darueber
        // das richtige Backend-Command. Sonst riefe ein als „erledigt"
        // dargestelltes, real aber `started`-Item beim „Ansehen" `ReplayTour`,
        // das der Backend-Guard nur aus completed/dismissed erlaubt
        // (INVALID_STATE_TRANSITION, Bug 2026-06-17).
        rawStatus: status,
        stepProgress,
        // Schluessel-Info fuer die "Wiederholung laeuft"-Anzeige: wenn
        // completedAt einen Wert hat, wurde die Tour mindestens einmal
        // echt durchlaufen — auch wenn der Status gerade auf 'started'
        // steht (Replay-Run). UI kann dann gruenes Haekchen + "Wiederholung"-
        // Kicker zeigen statt halben Kreis + "Jetzt dran".
        // autoCompletedAt zaehlt mit, damit ein "Tour ansehen"-Klick auf
        // einer AutoCompleted-Tour die Goal-Anerkennung nicht verliert
        // (transitionStarted setzt Status auf Started, autoCompletedAt
        // bleibt aber im Snapshot).
        wasEverCompleted: !!snap?.completedAt || !!snap?.autoCompletedAt,
        // Rohwert fuer den Done-Header-Datumsstempel. Bleibt ueber
        // transitionReplay() hinweg erhalten (siehe tour-state-store).
        completedAt: snap?.completedAt,
        isLocked,
        lockReasonTitleKeys,
        estimatedDurationSec: cfg?.estimatedDurationSec,
      };
    });
    this.tours = tours;

    // Einheitliche Aufteilung NUR nach Status (User-Wunsch 2026-06-17):
    // „Noch zu tun" (offen) vs. „Erledigt". Erledigt = selbst durchgespielt
    // (completed) ODER automatisch erkannt (auto-completed) ODER ein Replay,
    // der das Ziel schon erreicht hatte (started + wasEverCompleted). Beide
    // Erledigt-Arten sehen identisch aus + tragen EINE Aktion („Ansehen"),
    // statt frueher gruenes „Nochmal" oben und eine separate „Bereits
    // eingerichtet / Tour ansehen / Ueberspringen"-Sektion. Reihenfolge bleibt
    // die kanonische mainOrder.
    const isDoneItem = (t: (typeof tours)[number]): boolean =>
      t.status === TourStateStatus.Completed ||
      t.status === TourStateStatus.AutoCompleted ||
      (t.status === TourStateStatus.Started && !!t.wasEverCompleted);
    this.openTours = tours.filter(
      (t) => t.status !== TourStateStatus.Dismissed && !isDoneItem(t),
    );
    this.doneTours = tours.filter((t) => isDoneItem(t));
    this.dismissedTours = tours.filter(
      (t) => t.status === TourStateStatus.Dismissed,
    );
    // `primaryTours` bleibt die Quelle fuer isPrimaryNext („Als Naechstes") und
    // entspricht jetzt der Offen-Liste.
    this.primaryTours = this.openTours;
    // Weiterhin berechnet fuer evtl. andere Konsumenten; die UI rendert die
    // auto-completed Touren jetzt gemeinsam in der „Erledigt"-Sektion.
    this.autoCompletedTours = tours.filter(
      (t) => t.status === TourStateStatus.AutoCompleted,
    );

    // Aggregat-Werte aus OnboardingProgressService — Single Source of Truth,
    // identisch zur WelcomeCard. "Done" zaehlt Completed + AutoCompleted +
    // (Started && wasEverCompleted). Replay-Runs aendern den Counter NICHT.
    // Skipped/Dismissed zaehlt absichtlich nicht zum done — das ist eine
    // aktive User-Entscheidung gegen das Lernen, nicht das Erreichen.
    this.totalAll = progress.total;
    this.doneCount = progress.completed;
    this.progressPct = progress.progressPct;
    this.autoCount = this.autoCompletedTours.length;
    this.dismissedCount = this.dismissedTours.length;
    this.openCount = progress.total - progress.completed;

    // Done-State aus dem Service. Der `wasAllDoneBefore`-Vergleich bleibt
    // lokal, weil er das Just-Flipped-Signal fuer die Celebration-Animation
    // triggert.
    const wasAllDoneBefore = this.allDone;
    this.allDone = progress.allDone;
    this.recomputeViewMode(wasAllDoneBefore);
  }

  /**
   * Spaetester Completion-Zeitpunkt aus allen Touren — Quelle fuer den
   * "Abgeschlossen am ..."-Datumsstempel im Done-Header. Liefert null,
   * solange noch nicht alle 7 Schritte erledigt sind.
   */
  get setupCompletedAt(): Date | null {
    if (!this.allDone) return null;
    const dates = this.tours
      .map((t) => t.completedAt)
      .filter((d): d is Date => !!d)
      .map((d) => (d instanceof Date ? d : new Date(d)));
    if (dates.length === 0) return null;
    return dates.sort((a, b) => b.getTime() - a.getTime())[0];
  }

  /**
   * Bestimmt den aktuellen View-Modus. Wird sowohl aus `rebuild()`
   * (bei Daten-Aenderung) als auch aus `toggle()` (bei Open/Close) und
   * aus `dismissSetup()` / `restoreSetup()` aufgerufen.
   *
   * Replay-Resilienz: die Celebration laeuft nur, wenn `allDone` in diesem
   * Render-Zyklus von false auf true kippt UND der LocalStorage-Flag noch
   * nicht gesetzt ist. Bei Replay bleibt `allDone===true` (wegen
   * `wasEverCompleted`), `wasAllDoneBefore===true`, also kein Re-Trigger.
   */
  private recomputeViewMode(wasAllDoneBefore: boolean): void {
    if (this.setupDismissedAt) {
      this.viewMode = 'dismissed';
      return;
    }
    if (!this.allDone) {
      this.viewMode = this.open ? 'expanded' : 'collapsed-progress';
      this.celebrationPending = false;
      return;
    }
    // Done-State. Pending-Flag setzen, sobald `allDone` von false auf true
    // kippt und LocalStorage noch keinen Celebration-Marker hat. Das
    // ueberbrueckt den Fall, dass das Panel beim Uebergang zu ist
    // (uebliches Szenario: User hat gerade die letzte Tour beendet,
    // Tour-Coordinator hat das Panel beim Tour-Start automatisch
    // geschlossen). Trigger laeuft dann beim naechsten Oeffnen.
    const justFlipped = !wasAllDoneBefore && this.allDone;
    const seen = this.readLocalStorage(this.LS_KEY_CELEBRATION_SEEN);
    if (justFlipped && !seen) {
      this.celebrationPending = true;
    }
    if (this.open && this.celebrationPending) {
      this.viewMode = 'celebration';
      this.celebrating = true;
      this.celebrationPending = false;
      this.writeLocalStorage(
        this.LS_KEY_CELEBRATION_SEEN,
        new Date().toISOString(),
      );
      this.clearCelebrationTimer();
      this.celebrationTimer = window.setTimeout(() => {
        this.celebrating = false;
        this.celebrationTimer = null;
        this.cdr.markForCheck();
      }, 600);
    } else {
      this.viewMode = this.open ? 'celebration' : 'collapsed-done';
    }
  }

  /**
   * Wird vom "Setup ausblenden"-Link aufgerufen. Persistiert lokal,
   * blendet die Bubble sofort aus.
   */
  dismissSetup(): void {
    const now = new Date();
    this.setupDismissedAt = now;
    this.writeLocalStorage(this.LS_KEY_DISMISSED_AT, now.toISOString());
    this.open = false;
    this.viewMode = 'dismissed';
    this.cdr.markForCheck();
  }

  /**
   * Vom Help-Menue aufrufbar — entfernt den Dismissed-Status, die Bubble
   * erscheint wieder im passenden View-Modus.
   */
  restoreSetup(): void {
    this.setupDismissedAt = null;
    this.removeLocalStorage(this.LS_KEY_DISMISSED_AT);
    this.recomputeViewMode(this.allDone);
    this.cdr.markForCheck();
  }

  private clearCelebrationTimer(): void {
    if (this.celebrationTimer !== null) {
      clearTimeout(this.celebrationTimer);
      this.celebrationTimer = null;
    }
  }

  /**
   * LocalStorage-Wrapper: Browser-Sandbox/SSR-tolerant. In Capacitor-
   * WebViews und im Browser identisch verfuegbar; nur in Build-Time-SSR
   * (haben wir hier nicht) waere window undefined. Try/Catch absichert
   * gegen QuotaExceeded und disabled-storage-Modi.
   */
  private readLocalStorage(key: string): string | null {
    try {
      return typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem(key)
        : null;
    } catch {
      return null;
    }
  }

  private writeLocalStorage(key: string, value: string): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(key, value);
      }
    } catch {
      /* Quota- oder Disabled-Storage — leise ignorieren. */
    }
  }

  private removeLocalStorage(key: string): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(key);
      }
    } catch {
      /* leise ignorieren */
    }
  }
}
